import { ticketQueueTabId, OFFLINE_TAB_READY_EVENT } from '@/utils/offlineQueueTab';
import { useState, useEffect, useCallback, useReducer, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useTicket,
  useUpdateTicket,
  useTransitionTicket,
  useUnarchiveTicket,
  useSendToQbo,
  useDeleteTicket,
  useTicketAttachments,
  useUploadTicketAttachment,
  useDeleteTicketAttachment,
  useUpdateTicketAttachment,
  tickets,
  auth,
  geocoding,
  permissions,
  isTruckEquipmentType,
  parseCoordinatesText,
  ticketRecoveryReason,
  setTicketDetail,
} from '@field-tickets/shared';
import type {
  TicketStatus,
  LaborEntry,
  MaterialEntry,
  EquipmentEntry,
  Ticket,
  TicketRecoveryReason,
  TransitionPayload,
  UpdateTicketData,
  User,
} from '@field-tickets/shared';
import { showToast } from '@/components/Toast';
import { useAuthStore } from '@/store/auth';
import { useTicketRecovery } from '@/hooks/useTicketRecovery';
import {
  readTicketRecoveryDraft,
  recordTicketRecovery,
  writeTicketRecoveryDraft,
  buildTicketRecoveryDraft,
  ticketRecoveryDraftHoldsDurably,
  ticketRecoveryOwner,
  TICKET_RECOVERY_TAB_ID,
} from '@/utils/ticketRecovery';
import {
  buildTicketRetainedDraft,
  isTicketRetainedStorageKey,
  listTicketRetainedDrafts,
  newTicketRetainedSlot,
  removeTicketRetainedDraft,
  ticketRetainedDraftsMemoryOnly,
  writeTicketRetainedDraft,
  TICKET_RETAINED_EVENT,
  type TicketRetainedDraft,
} from '@/utils/ticketRetainedDrafts';
import { getAuthEpoch, isCurrentAuthEpoch } from '@/utils/authEpoch';
import { TICKET_SESSION_SNAPSHOT_EVENT, type TicketSessionSnapshot } from '@/utils/ticketSessionSnapshot';
import { isActorMismatch, isStaleSession, isTicketPutBlocked, sendTicketPut } from '@/utils/ticketPut';
import {
  extractBaserowSyncWarning,
  extractTransitionErrorMessage,
  isQboPushInProgressError,
} from '@/components/review/reviewErrors';

const QBO_PROGRESS_POLL_MS = 5_000;
const QBO_PROGRESS_TIMEOUT_MS = 10 * 60_000;
import {
  enqueueTicketSave,
  snapshotTicketSave,
  markQueuedTicketSaveFailed,
  persistTicketSaveSync,
  deleteTicketSave,
  createTicketSaveSnapshot,
  getQueuedTicketSave,
  getAllQueuedTicketSaves,
  ticketSaveEntryId,
  initializeOfflineQueueTab,
  isTicketQueueTabReady,
  isOfflineError,
  isTicketVersionConflict,
  changedTicketFieldsSinceBase,
  createTicketConflictSnapshot,
  editedTicketFieldsSinceBase,
  isTicketConflictField,
  rebaseTicketSaveForSystemChanges,
  payloadVersionToken,
  ticketVersionToken,
  withTicketSaveLock,
  withVersionToken,
  OFFLINE_SAVE_REPLAYED_EVENT,
  type QueuedTicketSave,
  type TicketSaveAdmissionIdentity,
  type TicketConflictSnapshot,
  type TicketSaveLease,
  type TicketVersionToken,
} from '@/utils/offlineQueue';

export function mergeGeocodeResult(
  current: Partial<UpdateTicketData>,
  result: { state?: string | null; county?: string | null },
): Partial<UpdateTicketData> {
  return {
    state: current.state || result.state || '',
    county: current.county || result.county || '',
  };
}

// Status-transition routes still return their established
// data.validation.errors contract. Keep that endpoint-specific parsing here;
// QBO push failures use reviewErrors.ts and its structured data.details shape.
function extractTicketTransitionErrorMessage(err: unknown): string {
  const apiErr = err as {
    message?: string;
    data?: { error?: string; message?: string; validation?: { errors?: string[] } } | null;
  } | undefined;
  const errors = apiErr?.data?.validation?.errors?.filter(Boolean) || [];
  if (errors.length === 1) return errors[0];
  if (errors.length > 1) return `${errors.length} validation errors: ${errors.join('; ')}`;
  return apiErr?.data?.message || apiErr?.message || apiErr?.data?.error ||
    (err instanceof Error ? err.message : '') || 'Failed to transition ticket';
}

export interface LaborRowErrors {
  hours?: string;
  employee_id?: string;
  /** Missing hourly rate — blocks invoicing (validateForInvoice). */
  hourly_rate?: string;
}

export interface MaterialRowErrors {
  quantity?: string;
  sku_name_snapshot?: string;
  qb_item_id?: string;
  line_type?: string;
  description?: string;
  /** Missing unit price with no QBO catalog fallback — blocks invoicing. */
  unit_price?: string;
  /** Flagged misc item not yet resolved — blocks invoicing. */
  review_required?: string;
}

export interface EquipmentRowErrors {
  equipment_type?: string;
  quantity?: string;
  hours?: string;
}

export type TicketFieldErrors = Partial<Record<
  | 'work_date'
  | 'description'
  | 'customer_id'
  | 'labor_entries'
  | 'material_entries'
  | 'billing_afe'
  | 'billing_requisitioner'
  | 'work_contact_name',
  string
>> & {
  labor_rows?: Record<number, LaborRowErrors>;
  material_rows?: Record<number, MaterialRowErrors>;
  equipment_rows?: Record<number, EquipmentRowErrors>;
};

type TicketDraftSnapshot = {
  formState: UpdateTicketData;
  laborEntries: LaborEntry[];
  memberIds: string[];
  materialEntries: MaterialEntry[];
  equipmentEntries: EquipmentEntry[];
};

type SaveStatus = 'clean' | 'dirty' | 'saving' | 'saved' | 'error' | 'queued';

const MANAGER_ONLY_UPDATE_FIELDS = [
  'billing_charge_code',
  'charge_code',
  'invoice_display_mode',
  'bill_to_needs_review',
] as const;

type LaborEntryWithLegacyRates = LaborEntry & {
  hourly_rate?: number | null;
};

type MaterialEntryWithPriceHints = MaterialEntry & {
  catalog_unit_price?: number | null;
};

type EquipmentEntryWithPriceAliases = EquipmentEntry & {
  unit_price?: number | null;
  catalog_unit_price?: number | null;
};

function stripManagerOnlyFields(formState: UpdateTicketData): UpdateTicketData {
  const scoped = { ...formState } as UpdateTicketData & Record<string, unknown>;
  for (const field of MANAGER_ONLY_UPDATE_FIELDS) {
    delete scoped[field];
  }
  return scoped;
}

function stripFinancialLaborRates(entry: LaborEntry): LaborEntry {
  const scoped = { ...entry } as LaborEntryWithLegacyRates;
  delete scoped.hourly_rate;
  delete scoped.hourly_rate_snapshot;
  return scoped as LaborEntry;
}

function stripManagerOnlyMaterialPrices(entry: MaterialEntry): MaterialEntry {
  const scoped = { ...entry } as MaterialEntryWithPriceHints;
  delete scoped.unit_price;
  delete scoped.unit_price_snapshot;
  delete scoped.catalog_unit_price;
  return scoped as MaterialEntry;
}

function stripManagerOnlyEquipmentPrices(entry: EquipmentEntry): EquipmentEntry {
  const scoped = { ...entry } as EquipmentEntryWithPriceAliases;
  delete scoped.unit_price;
  delete scoped.catalog_unit_price;
  if (!isTruckEquipmentType(scoped.equipment_type)) {
    delete scoped.unit_price_snapshot;
  }
  return scoped as EquipmentEntry;
}

function buildUpdatePayload(
  snapshot: TicketDraftSnapshot,
  includeManagerFields: boolean,
  includeFinancialPriceFields: boolean,
): UpdateTicketData {
  const formState = includeManagerFields
    ? snapshot.formState
    : stripManagerOnlyFields(snapshot.formState);
  const laborEntries = includeFinancialPriceFields
    ? snapshot.laborEntries
    : snapshot.laborEntries.map(stripFinancialLaborRates);
  const materialEntries = includeFinancialPriceFields
    ? snapshot.materialEntries
    : snapshot.materialEntries.map(stripManagerOnlyMaterialPrices);
  const equipmentEntries = includeFinancialPriceFields
    ? snapshot.equipmentEntries
    : snapshot.equipmentEntries.map(stripManagerOnlyEquipmentPrices);

  return {
    ...formState,
    labor_entries: [...laborEntries],
    member_ids: [...snapshot.memberIds],
    material_entries: [...materialEntries],
    equipment: [...equipmentEntries],
  };
}

/**
 * Inverse of buildUpdatePayload: split a queued offline payload back into the
 * form's draft snapshot shape so the on-open effect can re-hydrate the form
 * from edits that never reached the server. The collection fields are pulled
 * out of the flat payload; everything else is the scalar formState.
 */
function snapshotFromQueuedPayload(payload: UpdateTicketData): TicketDraftSnapshot {
  const {
    labor_entries,
    member_ids,
    material_entries,
    equipment,
    base_updated_at: _baseUpdatedAt,
    base_revision: _baseRevision,
    ...formState
  } = payload;
  return {
    formState: formState as UpdateTicketData,
    laborEntries: labor_entries ?? [],
    memberIds: member_ids ?? [],
    materialEntries: material_entries ?? [],
    equipmentEntries: equipment ?? [],
  };
}

/** A retained payload as form state, without the QuickBooks edit flags it was sent with. */
function snapshotFromRetainedPayload(payload: UpdateTicketData): TicketDraftSnapshot {
  const snapshot = snapshotFromQueuedPayload(payload);
  const formState = { ...snapshot.formState } as Record<string, unknown>;
  delete formState.qbo_edit_acknowledged;
  delete formState.qbo_apply_shared_to_group;
  return { ...snapshot, formState: formState as UpdateTicketData };
}

/** The ticket fields a conflict snapshot holds, shaped for snapshotFromTicket. */
function ticketFromConflictSnapshot(base: TicketConflictSnapshot): Ticket {
  const memberIds = Array.isArray(base.member_ids) ? base.member_ids as string[] : [];
  return { ...base, members: memberIds.map((employee_id) => ({ employee_id })) } as unknown as Ticket;
}

function sameVersionToken(left: TicketVersionToken, right: TicketVersionToken): boolean {
  if (typeof left.base_revision === 'number' || typeof right.base_revision === 'number') {
    return left.base_revision === right.base_revision;
  }
  return Boolean(left.base_updated_at) && left.base_updated_at === right.base_updated_at;
}

/** A retained draft (#1041) the open form offers to restore or discard. */
export interface TicketRetainedDraftView {
  slot: string;
  /** Kept by this browser tab (an earlier form), not another tab or session. */
  fromThisTab: boolean;
  /** Kept by a recovery Reload, not on leaving the ticket. */
  fromReload: boolean;
  createdAt: number;
  /** The edits as copyable text. */
  summary: string;
  /** The ticket has not changed since the edits' base: restore loads them as they were. */
  sameVersion: boolean;
  /** False when the ticket moved and the edits' base is unknown: they cannot be compared. */
  comparable: boolean;
  /** False when not comparable or nothing is left to reapply: copy or discard only. */
  canRestore: boolean;
  /** Fields the server changed since the edits' base (none when sameVersion). */
  serverChangedFields: string[];
  /** Fields these edits changed. */
  editedFields: string[];
  /** Edited fields whose value the ticket already has (e.g. an unconfirmed save that went through). */
  alreadySavedFields: string[];
  /**
   * Other edited fields the server changed too, or may have (any field the
   * conflict snapshot lacks): reapplying overwrites the ticket's value.
   */
  overlappingFields: string[];
}

/** Load-sync runs once per ticket revision (updated_at for an older backend). */
function ticketSyncKey(ticket: Ticket): string {
  return `${ticket.id}:${typeof ticket.revision === 'number' ? ticket.revision : ticket.updated_at}`;
}

function snapshotFromTicket(
  ticket: Ticket,
  currentMaterialEntries: MaterialEntry[],
): TicketDraftSnapshot {
  const customerId = ticket.customer_id || '';
  const customerName = ticket.customer_name || '';
  // Fall back to the serviced customer for legacy tickets, but prefer the raw
  // bill-to name snapshot so a reload cannot rewrite it with the serviced name.
  const billToCustomerId = ticket.bill_to_customer_id || ticket.customer_id || '';
  const billToCustomerName =
    ticket.bill_to_customer_name ||
    ticket.bill_to_customer_name_snapshot ||
    ticket.customer_name ||
    '';

  return {
    formState: {
      work_date: ticket.work_date || '',
      description: ticket.description || '',
      customer_id: customerId,
      customer_name: customerName,
      coordinates: ticket.coordinates || '',
      is_labor_only: ticket.is_labor_only ?? false,
      needs_work_order: ticket.needs_work_order ?? false,
      is_ine: ticket.is_ine ?? false,
      bill_to_customer_id: billToCustomerId,
      bill_to_customer_name: billToCustomerName,
      charge_code: ticket.charge_code || '',
      billing_requisitioner: ticket.billing_requisitioner || '',
      billing_afe: ticket.billing_afe || '',
      billing_notes: ticket.billing_notes || '',
      work_contact_name: ticket.work_contact_name || '',
      state: ticket.state || '',
      county: ticket.county || '',
    },
    laborEntries: ticket.labor_entries || [],
    memberIds: (ticket.members || []).map((member) => member.employee_id).filter(Boolean),
    materialEntries: preserveMaterialClientIds(
      ticket.material_entries || [],
      currentMaterialEntries,
    ),
    equipmentEntries: ticket.equipment || [],
  };
}

function materialIdentityKey(entry: MaterialEntry): string {
  const lineType = entry.line_type || (entry.qb_item_id ? 'sku' : 'misc');
  const price = entry.unit_price ?? entry.unit_price_snapshot ?? '';
  const quantity = entry.quantity ?? '';
  const provided = entry.is_customer_provided ? 'provided' : '';
  const bundleEntry = entry as MaterialEntry & {
    bundle_group_id?: string;
    bundle_label?: string;
  };
  const bundle = bundleEntry.bundle_group_id || bundleEntry.bundle_label || '';

  if (entry.qb_item_id) {
    return [
      lineType,
      entry.qb_item_id,
      String(quantity),
      String(price),
      entry.notes || '',
      provided,
      bundle,
    ].join('|');
  }

  // Persisted columns only. `item_name` is a client-side display alias the
  // server never returns, so keying on it dropped every new misc line's
  // _clientId on its first save, which closed the line's detail drawer
  // mid-typing on phones (where it is the only description field).
  return [
    lineType,
    entry.qb_item_id || '',
    entry.sku_name_snapshot || '',
    entry.description || '',
    entry.notes || '',
    String(quantity),
    String(price),
    provided,
    bundle,
  ].join('|');
}

export function preserveMaterialClientIds(
  incomingEntries: MaterialEntry[],
  currentEntries: MaterialEntry[],
): MaterialEntry[] {
  if (incomingEntries.length === 0 || currentEntries.length === 0) {
    return incomingEntries;
  }

  const currentById = new Map(
    currentEntries
      .filter((entry) => entry.id && entry._clientId)
      .map((entry) => [entry.id as string, entry._clientId as string]),
  );
  const unusedCurrent = currentEntries.map((entry, index) => ({ entry, index, used: false }));

  return incomingEntries.map((incoming, incomingIndex) => {
    const idMatch = incoming.id ? currentById.get(incoming.id) : undefined;
    if (idMatch) {
      const matchedCurrent = unusedCurrent.find((candidate) =>
        !candidate.used &&
        candidate.entry.id === incoming.id &&
        candidate.entry._clientId === idMatch,
      );
      if (matchedCurrent) {
        matchedCurrent.used = true;
      }
      return { ...incoming, _clientId: idMatch };
    }

    const incomingKey = materialIdentityKey(incoming);
    let match = unusedCurrent.find((candidate) =>
      !candidate.used &&
      candidate.entry._clientId &&
      materialIdentityKey(candidate.entry) === incomingKey,
    );

    if (!match) {
      const positional = unusedCurrent[incomingIndex];
      if (
        positional &&
        !positional.used &&
        positional.entry._clientId &&
        materialIdentityKey(positional.entry) === incomingKey
      ) {
        match = positional;
      }
    }

    if (!match) {
      return incoming;
    }

    match.used = true;
    return { ...incoming, _clientId: match.entry._clientId };
  });
}

const TRANSITION_FIELD_LABELS: Record<string, string> = {
  work_date: 'Work date',
  description: 'Description',
  customer_id: 'Customer',
  labor_entries: 'Labor hours',
  material_entries: 'Materials',
  billing_afe: 'AFE',
  billing_requisitioner: 'Requisitioner',
  work_contact_name: 'Work Contact Name',
};

export function describeMissingTransitionFields(errors: TicketFieldErrors): string {
  const labels = Object.keys(errors)
    .filter((key) => key !== 'labor_rows' && key !== 'material_rows' && key !== 'equipment_rows')
    .map((key) => TRANSITION_FIELD_LABELS[key] ?? key);
  if (labels.length === 0) return 'Fix the highlighted fields before submitting';
  if (labels.length === 1) return `Please fill out: ${labels[0]}`;
  if (labels.length === 2) return `Please fill out: ${labels[0]} and ${labels[1]}`;
  return `Please fill out: ${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

/**
 * Parse a server-side `validation_failed` payload's `details[]` into the
 * shape `TicketFieldErrors` expects. The backend emits paths like
 * `labor[0].hours` or `materials[2].quantity` (see
 * backend/src/utils/request-validation.js); we surface those at the
 * matching row + field so the row component can render an inline error
 * instead of the user staring at a generic "Failed to save" toast.
 */
export function parseValidationDetails(
  details: ReadonlyArray<{ path?: string; field?: string; message?: string }>,
): Pick<TicketFieldErrors, 'labor_rows' | 'material_rows' | 'equipment_rows'> {
  const labor_rows: Record<number, LaborRowErrors> = {};
  const material_rows: Record<number, MaterialRowErrors> = {};
  const equipment_rows: Record<number, EquipmentRowErrors> = {};

  for (const detail of details) {
    if (!detail || typeof detail.message !== 'string') continue;
    const path = typeof detail.path === 'string' ? detail.path : '';
    const match = /^(labor|materials|equipment)\[(\d+)\](?:\.(\w+))?$/.exec(path);
    if (!match) continue;
    const [, group, indexStr, fieldName] = match;
    const index = Number(indexStr);
    if (!Number.isFinite(index)) continue;

    if (group === 'labor') {
      const existing = labor_rows[index] ?? {};
      const key = (fieldName as keyof LaborRowErrors) || 'hours';
      existing[key] = detail.message;
      labor_rows[index] = existing;
    } else {
      if (group === 'equipment') {
        const existing = equipment_rows[index] ?? {};
        const key = (fieldName as keyof EquipmentRowErrors) || 'equipment_type';
        existing[key] = detail.message;
        equipment_rows[index] = existing;
        continue;
      }
      const existing = material_rows[index] ?? {};
      const key = (fieldName as keyof MaterialRowErrors) || 'line_type';
      existing[key] = detail.message;
      material_rows[index] = existing;
    }
  }

  const out: Pick<TicketFieldErrors, 'labor_rows' | 'material_rows' | 'equipment_rows'> = {};
  if (Object.keys(labor_rows).length > 0) out.labor_rows = labor_rows;
  if (Object.keys(material_rows).length > 0) out.material_rows = material_rows;
  if (Object.keys(equipment_rows).length > 0) out.equipment_rows = equipment_rows;
  return out;
}

/**
 * Whether `ticket` is a strictly newer version than `refused` (#1044). A
 * conflict's rebase adopts only a version the server has moved past the one
 * it refused; an equal or older read (a cached copy) proves nothing.
 */
function isNewerThanVersionToken(ticket: Ticket, refused: TicketVersionToken): boolean {
  if (typeof refused.base_revision === 'number') {
    return typeof ticket.revision === 'number' && ticket.revision > refused.base_revision;
  }
  if (refused.base_updated_at && ticket.updated_at) {
    return Date.parse(ticket.updated_at) > Date.parse(refused.base_updated_at);
  }
  return false;
}

export function useTicketForm(ticketId: string, user: User | null) {
  const { data: ticket, isLoading, isError, error, refetch } = useTicket(ticketId);
  const [attachmentsLimit, setAttachmentsLimit] = useState(50);
  const { data: attachmentsPage } = useTicketAttachments(ticketId, { limit: attachmentsLimit, offset: 0 });
  const attachments = attachmentsPage?.attachments ?? [];
  const attachmentsTotal = attachmentsPage?.total ?? attachments.length;
  const attachmentsHasMore = attachmentsPage?.has_more ?? false;
  const loadMoreAttachments = useCallback(() => {
    setAttachmentsLimit((prev) => Math.min(prev + 50, 200));
  }, []);
  const uploadAttachment = useUploadTicketAttachment(ticketId, ticket?.site_id);
  const deleteAttachment = useDeleteTicketAttachment(ticketId, ticket?.site_id);
  const updateAttachment = useUpdateTicketAttachment(ticketId, ticket?.site_id);
  const inflightAbortRef = useRef<AbortController | null>(null);
  // The save the autosave mutation is sending for: its lock lease and the
  // session epoch it started in. Every PUT goes through sendTicketPut (#967).
  const saveContextRef = useRef<{ lease: TicketSaveLease; epoch: number; owner: string } | null>(null);
  const updateTicket = useUpdateTicket(ticketId, ticket?.card_id, {
    getAbortSignal: () => inflightAbortRef.current?.signal,
    put: async (data, signal) => {
      const context = saveContextRef.current;
      if (!context) throw new Error('Ticket save sent outside a save');
      const updated = await sendTicketPut(context.lease, data, { epoch: context.epoch, owner: context.owner, signal });
      // A switched-away response must not reach the mutation's cache callback,
      // whose options now belong to the next ticket on this hook instance.
      if (signal?.aborted || saveContextRef.current !== context) {
        throw new DOMException('Ticket save cancelled', 'AbortError');
      }
      return updated;
    },
  });
  const transitionTicket = useTransitionTicket(ticketId, ticket?.card_id);
  const unarchiveTicket = useUnarchiveTicket(ticketId, ticket?.card_id);
  const sendToQboMutation = useSendToQbo(ticketId, ticket?.card_id);
  const deleteTicketMutation = useDeleteTicket(ticketId, ticket?.card_id, ticket?.site_id);

  // Local form state
  const [formState, setFormState] = useState<UpdateTicketData>({});
  const [laborEntries, setLaborEntries] = useState<LaborEntry[]>([]);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [materialEntries, setMaterialEntries] = useState<MaterialEntry[]>([]);
  const [equipmentEntries, setEquipmentEntries] = useState<EquipmentEntry[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('clean');
  const [qboPushError, setQboPushError] = useState<string | null>(null);
  const [qboPushFeedbackByTicketId, setQboPushFeedbackByTicketId] = useState<Record<
    string,
    { warning: string | null; verificationRequired: boolean }
  >>({});
  const [qboPushTicketIds, setQboPushTicketIds] = useState<string[]>([]);
  const currentQboPushFeedback = qboPushFeedbackByTicketId[ticketId];
  const qboPushWarning = currentQboPushFeedback?.warning ?? null;
  const qboPushVerificationRequired = currentQboPushFeedback?.verificationRequired ?? false;
  const setQboPushWarning = useCallback((
    next: string | null | ((current: string | null) => string | null),
  ) => {
    setQboPushFeedbackByTicketId((current) => {
      const previous = current[ticketId] ?? { warning: null, verificationRequired: false };
      const warning = typeof next === 'function' ? next(previous.warning) : next;
      return { ...current, [ticketId]: { ...previous, warning } };
    });
  }, [ticketId]);
  const setQboPushVerificationRequired = useCallback((verificationRequired: boolean) => {
    setQboPushFeedbackByTicketId((current) => {
      const previous = current[ticketId] ?? { warning: null, verificationRequired: false };
      return { ...current, [ticketId]: { ...previous, verificationRequired } };
    });
  }, [ticketId]);
  const qboPushInProgress = qboPushTicketIds.includes(ticketId);

  useEffect(() => {
    if (qboPushTicketIds.length === 0) return;
    let cancelled = false;

    const checkProgress = async () => {
      try {
        const refreshedTickets = await Promise.all(
          qboPushTicketIds.map((trackedId) => tickets.get(trackedId)),
        );
        if (cancelled) return;
        const completedIds = refreshedTickets
          .filter((refreshed) => refreshed.qb_invoice_id || refreshed.qbo_estimate_id)
          .map((refreshed) => refreshed.id);
        const releasedIds = refreshedTickets
          .filter((refreshed) =>
            !refreshed.qb_invoice_id &&
            !refreshed.qbo_estimate_id &&
            refreshed.qbo_push_in_progress === false
          )
          .map((refreshed) => refreshed.id);
        if (completedIds.length || releasedIds.length) {
          const settledIds = new Set([...completedIds, ...releasedIds]);
          setQboPushTicketIds((current) => current.filter((id) => !settledIds.has(id)));
          setQboPushFeedbackByTicketId((current) => {
            const next = { ...current };
            for (const id of completedIds) delete next[id];
            for (const id of releasedIds) {
              next[id] = {
                warning: 'The earlier QuickBooks attempt ended without linking a document. Review the ticket, then try again when ready.',
                verificationRequired: false,
              };
            }
            return next;
          });
          if (completedIds.length) {
            showToast(
              completedIds.includes(ticketId)
                ? 'QuickBooks finished creating the document'
                : 'QuickBooks finished creating the document for an earlier ticket',
              'success',
            );
          }
          if (settledIds.has(ticketId)) void refetch();
        }
      } catch {
        // A transient read failure must not unlock another send.
      }
    };

    void checkProgress();
    const pollId = window.setInterval(() => {
      void checkProgress();
    }, QBO_PROGRESS_POLL_MS);
    const timeoutId = window.setTimeout(() => {
      const message =
        'QuickBooks is taking longer than expected. Sending remains locked while we keep checking; verify QuickBooks before trying again.';
      setQboPushFeedbackByTicketId((current) => {
        const next = { ...current };
        for (const id of qboPushTicketIds) {
          next[id] = { warning: message, verificationRequired: true };
        }
        return next;
      });
    }, QBO_PROGRESS_TIMEOUT_MS);

    return () => {
      cancelled = true;
      window.clearInterval(pollId);
      window.clearTimeout(timeoutId);
    };
  }, [
    qboPushTicketIds,
    ticketId,
    refetch,
  ]);

  useEffect(() => {
    if (
      qboPushInProgress &&
      (ticket?.qb_invoice_id || ticket?.qbo_estimate_id)
    ) {
      setQboPushTicketIds((current) => current.filter((id) => id !== ticketId));
      setQboPushFeedbackByTicketId((current) => {
        const next = { ...current };
        delete next[ticketId];
        return next;
      });
      showToast('QuickBooks finished creating the document', 'success');
    }
  }, [qboPushInProgress, ticketId, ticket?.qb_invoice_id, ticket?.qbo_estimate_id]);

  useEffect(() => {
    if (ticket?.qbo_push_in_progress && !ticket.qb_invoice_id && !ticket.qbo_estimate_id) {
      setQboPushTicketIds((current) => current.includes(ticketId) ? current : [...current, ticketId]);
      setQboPushWarning((current) => current || 'QuickBooks is already creating this document. We’ll keep checking for it; please do not send it again.');
    }
  }, [ticketId, ticket?.qbo_push_in_progress, ticket?.qb_invoice_id, ticket?.qbo_estimate_id]);
  // Mirror saveStatus into a ref so handleTransition can check the latest
  // value after awaiting flushPendingSave without re-rendering the closure.
  const saveStatusRef = useRef(saveStatus);
  saveStatusRef.current = saveStatus;
  // Guards handleTransition against double-submit during the flush-before-move
  // window now that the Move buttons stay enabled with unsaved changes (#409).
  const transitionInFlightRef = useRef(false);
  const setSaveStatusValue = useCallback((nextStatus: SaveStatus) => {
    saveStatusRef.current = nextStatus;
    setSaveStatus(nextStatus);
  }, []);
  const [fieldErrors, setFieldErrors] = useState<TicketFieldErrors>({});
  // Incremented only when a transition click is rejected by client-side
  // validation. Renderers use this event-like counter to scroll once per
  // rejected attempt without scrolling again as errors clear while typing.
  const [transitionRejectionCount, setTransitionRejectionCount] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const savedFlashRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lastSyncedRef = useRef<string | null>(null);
  // The version token (#967: base_revision) of the server ticket the form's
  // edits are based on, mirrored into a ref so the detached unmount/teardown
  // flush can pin its payload without an extra pre-fetch GET. The ticket
  // itself never goes back: the detail cache keeps the highest revision
  // (keepNewerTicket in @field-tickets/shared).
  const ticketVersionRef = useRef<TicketVersionToken>({});
  const ticketBaseSnapshotRef = useRef<TicketConflictSnapshot | undefined>(undefined);
  // The payload the server holds at that base, as this form would send it
  // (#1041): the baseline a retained draft's edits are measured against,
  // including fields the conflict snapshot lacks (bill_to_needs_review).
  // Unknown (undefined) after a queued-save restore or a system rebase.
  const ticketBasePayloadRef = useRef<Record<string, unknown> | undefined>(undefined);
  const lastGeocodedCoordinatesRef = useRef<{
    raw: string;
    normalized: string;
  } | null>(null);
  const draftSnapshotRef = useRef<TicketDraftSnapshot>({
    formState,
    laborEntries,
    memberIds,
    materialEntries,
    equipmentEntries,
  });
  const draftVersionRef = useRef(0);
  const incorporatedAdmissionsRef = useRef(new Map<string, TicketSaveAdmissionIdentity>());
  const incorporateAdmission = useCallback((entry: QueuedTicketSave) => {
    for (const identity of [entry, ...(entry.incorporatedAdmissions ?? [])]) {
      if (identity.owner !== entry.owner || identity.ticketId !== entry.ticketId) continue;
      const { owner, ticketId, tabId, sequence } = identity;
      incorporatedAdmissionsRef.current.set(ticketSaveEntryId(identity), { owner, ticketId, tabId, sequence });
    }
  }, []);
  const queueReconciliationRef = useRef<{ ticketId: string; complete: boolean; promise?: Promise<void> }>({ ticketId, complete: false });
  // Identity is immutable for this mount. The host remounts on owner/epoch
  // changes; stale callbacks must never adopt the next account's identity.
  const recoveryOwnerRef = useRef(ticketRecoveryOwner(user));
  const recoveryOwner = recoveryOwnerRef.current;
  // A dirty snapshot keeps its author even if auth changes before page-hide.
  const draftOwnerRef = useRef(recoveryOwner);
  // This form session's retained-draft slot (#1041): hiding or closing a
  // dirty QuickBooks-linked form keeps its edits there. New per ticket, so a
  // form never overwrites edits an earlier form kept, and never offers its
  // own (still on screen) back for restore.
  const retainSlotRef = useRef(newTicketRetainedSlot());
  const retainOwnEdits = useCallback((owner: string, id: string, payload: UpdateTicketData, baseSnapshot?: TicketConflictSnapshot) => {
    const draft = buildTicketRetainedDraft(owner, id, retainSlotRef.current, payload, baseSnapshot, ticketBasePayloadRef.current);
    return { draft, durable: writeTicketRetainedDraft(draft) };
  }, []);
  // A legacy edit may be restored for review, but merely opening/closing the
  // form must not turn its manual-retry marker into an automatic save.
  const manualRetryRequiredRef = useRef(false);
  const actorMismatchRef = useRef(false);
  const [explicitRecovery, setExplicitRecovery] = useState<QueuedTicketSave | null>(null);
  // This form belongs to its mount session, even if auth changes before cleanup.
  const formSessionEpochRef = useRef(getAuthEpoch());
  const queryClient = useQueryClient();
  const editingFrozen = useAuthStore((state) => state.ticketEditingFrozenEpoch === formSessionEpochRef.current);
  const [, setQueueReady] = useState(isTicketQueueTabReady());
  useEffect(() => {
    const loaded = () => setQueueReady(isTicketQueueTabReady());
    window.addEventListener(OFFLINE_TAB_READY_EVENT, loaded);
    void initializeOfflineQueueTab().then(loaded);
    return () => window.removeEventListener(OFFLINE_TAB_READY_EVENT, loaded);
  }, []);
  const canEditSession = useCallback(() => isTicketQueueTabReady() && isCurrentAuthEpoch(formSessionEpochRef.current)
    && useAuthStore.getState().ticketEditingFrozenEpoch !== formSessionEpochRef.current, []);
  // Tracks whether the most recent autosave attempt failed, so we suppress
  // duplicate toasts while the form is still in the same error streak. We
  // still surface the error via `saveStatus='error'` and the per-field
  // inline messages, but the toast only fires on the first failure or after
  // a successful save resets the streak.
  const errorStreakRef = useRef(false);

  const canViewRates = user ? permissions.canViewRates(user) : false;
  const canEditWorkspace = user ? permissions.canEditWorkspace(user) : false;
  const canPushToQbo = user ? permissions.canPushToQbo(user) : false;
  // Estimate-flow gates (mirror the backend): convert = convert_estimate OR
  // manage_invoices (Sales included); estimate recall/DELETE = manage_invoices
  // (manager/admin only); sales pdf/send access = sales_view.
  const canConvertEstimate = user ? permissions.canConvertEstimate(user) : false;
  const canManageInvoices = user ? permissions.canManageInvoices(user) : false;
  const canViewSales = user ? permissions.canViewSales(user) : false;
  const canDeleteAttachments = Boolean(user?.isManager || user?.isAdmin);
  const isManagerOrAdmin = Boolean(user?.isManager || user?.isAdmin);
  const canEditOfficeFields = isManagerOrAdmin || user?.role === 'sales';
  const formPayloadOf = (snapshot: TicketDraftSnapshot): Record<string, unknown> =>
    buildUpdatePayload(snapshot, canEditOfficeFields, canViewRates) as Record<string, unknown>;
  const techDeletableStatuses = ['draft', 'field_review'];
  const canDelete = canEditWorkspace
    && (isManagerOrAdmin || (ticket ? techDeletableStatuses.includes(ticket.status) : false));
  const isAwaitingManagerReview = Boolean(ticket?.status === 'field_review' && !isManagerOrAdmin);
  // Self-recall (#466): server-computed — the submitter may pull their own
  // ticket back to draft while it's still awaiting review.
  const canSelfRecall = Boolean(
    ticket?.status === 'field_review' && ticket?.permissions?.canSelfRecall,
  );
  // Server says whether this viewer may edit (owner/assignee/region/manager).
  // Absent on legacy payloads → treat as editable. A view-only member sees the
  // same read-only form as a locked/awaiting-review ticket.
  const canEditTicket = ticket?.permissions?.canEdit ?? true;
  const isQboLinked = Boolean(ticket?.qbo_sync?.linked || ticket?.qb_invoice_id || ticket?.qbo_estimate_id);
  const canResyncQboTickets = Boolean(ticket?.permissions?.canResyncQboTickets && ticket?.qbo_sync);
  const [qboEditEnabled, setQboEditEnabled] = useState(false);
  useEffect(() => setQboEditEnabled(false), [ticketId]);
  const qboDocumentLocked = Boolean(ticket?.is_locked && !(canResyncQboTickets && qboEditEnabled));
  const isLocked = qboDocumentLocked || isAwaitingManagerReview || !canEditTicket || editingFrozen;

  const beginQboEdit = useCallback(() => {
    if (!canEditSession()) return false;
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      showToast('Reconnect before editing a QuickBooks-linked ticket', 'error');
      return false;
    }
    setQboEditEnabled(true);
    return true;
  }, []);

  const cancelQboEdit = useCallback(() => {
    if (!canEditSession()) return false;
    if (isDirtyRef.current && !window.confirm('Discard the unsaved changes in this ticket?')) return false;
    setQboEditEnabled(false);
    void refetch();
    return true;
  }, [refetch]);

  const buildCurrentUpdatePayload = useCallback(() => {
    const payload = buildUpdatePayload(
      draftSnapshotRef.current,
      canEditOfficeFields,
      canViewRates,
    );
    if (isQboLinked && qboEditEnabled && (ticket?.qbo_sync?.group_ticket_count || 0) > 1) {
      payload.qbo_apply_shared_to_group = true;
    }
    if (isQboLinked && qboEditEnabled) payload.qbo_edit_acknowledged = true;
    return payload;
  }, [canEditOfficeFields, canViewRates, isQboLinked, qboEditEnabled, ticket?.qbo_sync?.group_ticket_count]);

  // Sync server data into local state on initial load or after a save.
  // IMPORTANT: skip when form is dirty to prevent overwriting user input
  // during background refetches (e.g. React Query stale refresh).
  const isDirtyRef = useRef(false);
  isDirtyRef.current = isDirty;

  const markDirty = useCallback(() => {
    if (!isDirtyRef.current) draftOwnerRef.current = recoveryOwnerRef.current;
    draftVersionRef.current += 1;
    isDirtyRef.current = true;
    clearTimeout(savedFlashRef.current);
    savedFlashRef.current = undefined;
    setIsDirty(true);
    setSaveStatusValue(manualRetryRequiredRef.current ? 'error' : 'dirty');
  }, [setSaveStatusValue]);

  // The recovery state machine (#967). Every write path (save, status
  // change, QuickBooks send, offline replay) enters it through
  // enterRecoveryReason; while recoveringRef.current is true the form is
  // read-only and nothing saves, queues or replays the draft. The only exit
  // is its confirmed network read (handleReloadLatest).
  // The teardown effect preserves the outgoing ticket's pending edits before
  // this reset. Recovery edits stay in their durable draft, never replayed.
  // Run before useTicketRecovery so the new ticket restores only its own edits.
  const formTicketIdRef = useRef(ticketId);
  const formGenerationRef = useRef(0);
  useEffect(() => {
    if (formTicketIdRef.current === ticketId) return;
    formTicketIdRef.current = ticketId;
    incorporatedAdmissionsRef.current.clear();
    retainSlotRef.current = newTicketRetainedSlot();
    manualRetryRequiredRef.current = false;
    actorMismatchRef.current = false;
    setExplicitRecovery(null);
    formGenerationRef.current += 1;
    clearTimeout(debounceRef.current);
    clearTimeout(savedFlashRef.current);
    debounceRef.current = undefined;
    savedFlashRef.current = undefined;
    saveRequestedRef.current = false;
    saveLoopPromiseRef.current = null;
    isDirtyRef.current = false;
    setIsDirty(false);
    const emptySnapshot: TicketDraftSnapshot = {
      formState: {}, laborEntries: [], memberIds: [], materialEntries: [], equipmentEntries: [],
    };
    draftSnapshotRef.current = emptySnapshot;
    draftVersionRef.current += 1;
    setFormState(emptySnapshot.formState);
    setLaborEntries(emptySnapshot.laborEntries);
    setMemberIds(emptySnapshot.memberIds);
    setMaterialEntries(emptySnapshot.materialEntries);
    setEquipmentEntries(emptySnapshot.equipmentEntries);
    lastSyncedRef.current = null;
    ticketVersionRef.current = {};
    ticketBaseSnapshotRef.current = undefined;
    ticketBasePayloadRef.current = undefined;
    lastGeocodedCoordinatesRef.current = null;
    errorStreakRef.current = false;
    setFieldErrors({});
    setTransitionRejectionCount(0);
    transitionInFlightRef.current = false;
    setQboPushError(null);
    setAttachmentsLimit(50);
    setSaveStatusValue('clean');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs and stable setters
  }, [ticketId]);

  const recoveryApi = useTicketRecovery(ticketId, recoveryOwner, {
    sessionEpoch: formSessionEpochRef.current,
    currentEdits: () => {
      const payload = withVersionToken(buildCurrentUpdatePayload(), ticketVersionRef.current);
      return {
        payload,
        baseSnapshot: ticketBaseSnapshotRef.current,
        basePayload: ticketBasePayloadRef.current,
        dirty: isDirtyRef.current,
      };
    },
    onEnter: (draft, { hydrate }) => {
      draftOwnerRef.current = draft.owner;
      clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
      if (hydrate) {
        // Reopened (or entered by the replay): show the draft's edits.
        const snapshot = snapshotFromQueuedPayload(draft.payload);
        ticketVersionRef.current = payloadVersionToken(draft.payload);
        ticketBaseSnapshotRef.current = draft.baseSnapshot;
        ticketBasePayloadRef.current = draft.basePayload;
        draftSnapshotRef.current = snapshot;
        draftVersionRef.current += 1;
        setFormState(snapshot.formState);
        setLaborEntries(snapshot.laborEntries);
        setMemberIds(snapshot.memberIds);
        setMaterialEntries(snapshot.materialEntries);
        setEquipmentEntries(snapshot.equipmentEntries);
      }
      isDirtyRef.current = true;
      setIsDirty(true);
      setSaveStatusValue('error');
    },
    adopt: (currentTicket) => {
      incorporatedAdmissionsRef.current.clear();
      // The edits this form kept on leaving are in its recovery draft (the
      // same or newer: recovery blocks edits), which the reload keeps.
      removeTicketRetainedDraft(draftOwnerRef.current, currentTicket.id, retainSlotRef.current);
      draftOwnerRef.current = recoveryOwnerRef.current;
      const snapshot = snapshotFromTicket(
        currentTicket,
        draftSnapshotRef.current.materialEntries,
      );
      clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
      saveRequestedRef.current = false;
      draftSnapshotRef.current = snapshot;
      draftVersionRef.current += 1;
      setFormState(snapshot.formState);
      setLaborEntries(snapshot.laborEntries);
      setMemberIds(snapshot.memberIds);
      setMaterialEntries(snapshot.materialEntries);
      setEquipmentEntries(snapshot.equipmentEntries);
      setFieldErrors({});
      ticketVersionRef.current = ticketVersionToken(currentTicket);
      ticketBaseSnapshotRef.current = createTicketConflictSnapshot(currentTicket);
      ticketBasePayloadRef.current = formPayloadOf(snapshot);
      // The cache now holds this same ticket; load-sync must not re-run on it.
      lastSyncedRef.current = ticketSyncKey(currentTicket);
      errorStreakRef.current = false;
      isDirtyRef.current = false;
      setIsDirty(false);
      setSaveStatusValue('clean');
    },
  });
  const { recovery, recoveringRef, enter: enterRecoveryReason, isBlocked: isRecoveryBlocked } = recoveryApi;
  const reloadRequired = recovery !== null;
  // Inputs follow this, not isLocked: a reload-required form is read-only too.
  const isEditBlocked = isLocked || reloadRequired || !canEditSession();

  useEffect(() => {
    if (ticket) {
      const syncKey = ticketSyncKey(ticket);
      if (lastSyncedRef.current === syncKey) return;
      if (isDirtyRef.current) return; // Don't overwrite active edits
      // A reload-required form leaves only through a confirmed network read.
      if (recoveringRef.current) return;
      lastSyncedRef.current = syncKey;

      const nextSnapshot = snapshotFromTicket(ticket, draftSnapshotRef.current.materialEntries);
      incorporatedAdmissionsRef.current.clear();
      draftOwnerRef.current = recoveryOwnerRef.current;
      draftSnapshotRef.current = nextSnapshot;
      draftVersionRef.current += 1;

      setFormState(nextSnapshot.formState);
      setLaborEntries(nextSnapshot.laborEntries);
      setMemberIds(nextSnapshot.memberIds);
      setMaterialEntries(nextSnapshot.materialEntries);
      setEquipmentEntries(nextSnapshot.equipmentEntries);
      isDirtyRef.current = false;
      setIsDirty(false);
      setSaveStatusValue('clean');
      ticketVersionRef.current = ticketVersionToken(ticket);
      ticketBaseSnapshotRef.current = createTicketConflictSnapshot(ticket);
      ticketBasePayloadRef.current = formPayloadOf(nextSnapshot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formPayloadOf reads only the role scope
  }, [setSaveStatusValue, ticket, canEditOfficeFields, canViewRates]);

  // On open, re-hydrate the form FROM a previously-queued offline save for this
  // ticket (and mark it dirty), then show "Queued offline" on the pill.
  //
  // A queued payload is by construction strictly NEWER than the server row: it
  // holds edits whose PUT failed offline and never landed. The load-sync effect
  // above hydrates from `ticket` (the React Query cache), which after a failed
  // offline PUT still holds STALE server data missing those edits. If we only
  // flipped the pill to 'queued' (the prior behavior) the tech would see stale
  // server state under a "Queued offline" label, and the next field edit —
  // built from the stale draftSnapshotRef — would OVERWRITE the queued payload
  // (last-write-wins), silently losing the original edits. Replay only runs on
  // app load or the 'online' event, so during a sustained offline session it
  // never closes this gap. Hydrating from the queued payload here lets the tech
  // see and continue their pending edits instead. Marking dirty also stops a
  // background refetch's load-sync from clobbering the queued edits.
  useEffect(() => {
    if (!ticketId) return;
    const reconciliation: typeof queueReconciliationRef.current = { ticketId, complete: false };
    queueReconciliationRef.current = reconciliation;
    if (isQboLinked) {
      // Office/QBO correction is intentionally online-only. Never replay a
      // stale linked-document snapshot into this form or discard it on open.
      reconciliation.complete = true;
      return;
    }
    let cancelled = false;
    const queued = explicitRecovery ? Promise.resolve(explicitRecovery) : getQueuedTicketSave(recoveryOwner, ticketId);
    reconciliation.promise = Promise.all([queued, getAllQueuedTicketSaves(recoveryOwner)]).then(([entry, entries]) => {
      if (cancelled || !isCurrentAuthEpoch(formSessionEpochRef.current)) return;
      const rows = entries.filter((row) => row.ticketId === ticketId);
      if (entry && !rows.some((row) => ticketSaveEntryId(row) === ticketSaveEntryId(entry))) rows.push(entry);
      const hasConflict = rows.some((row) => row.failure?.code === 'ticket_version_conflict');
      for (const row of rows.filter((row) => hasConflict
        || ((isDirtyRef.current || saveStatusRef.current === 'saving') && !incorporatedAdmissionsRef.current.has(ticketSaveEntryId(row)))
        || (row.tabId !== ticketQueueTabId() && !row.tabId.startsWith('legacy:')))) {
        // Recovery preserves both the stored variant and any live edits made
        // before reconciliation; neither draft silently wins over the other.
        recordTicketRecovery(recoveryOwner, ticketId, 'conflict', row.payload, row.baseSnapshot, ticketSaveEntryId(row));
      }
      if (cancelled || !entry || !isCurrentAuthEpoch(formSessionEpochRef.current)) return;
      // A recovery draft holds the same or newer edits and wins.
      if (recoveringRef.current) return;
      // Live edits have already been preserved beside unincorporated entries.
      if (isDirtyRef.current || saveStatusRef.current === 'saving') return;

      const snapshot = snapshotFromQueuedPayload(entry.payload);
      incorporateAdmission(entry);
      draftOwnerRef.current = entry.owner;
      // Keep the queued save's original OCC base. Using the freshly-fetched
      // ticket token here would silently turn replay into last-write-wins.
      ticketVersionRef.current = payloadVersionToken(entry.payload);
      ticketBaseSnapshotRef.current = entry.baseSnapshot;
      ticketBasePayloadRef.current = undefined;
      draftSnapshotRef.current = snapshot;
      draftVersionRef.current += 1;
      setFormState(snapshot.formState);
      setLaborEntries(snapshot.laborEntries);
      setMemberIds(snapshot.memberIds);
      setMaterialEntries(snapshot.materialEntries);
      setEquipmentEntries(snapshot.equipmentEntries);
      // Dirty (so load-sync won't re-hydrate stale server data over these
      // edits) but the pill stays 'queued' — truthful: the server doesn't have
      // them yet. Replay / unmount-flush persists them when connectivity allows.
      isDirtyRef.current = true;
      setIsDirty(true);
      setSaveStatusValue(entry.failure ? 'error' : 'queued');
      actorMismatchRef.current = entry.failure?.code === 'actor_mismatch';
      manualRetryRequiredRef.current = actorMismatchRef.current || entry.failure?.code === 'legacy_manual_retry';
      if (manualRetryRequiredRef.current) showToast(entry.failure!.message, 'warning');
      // A replay that ended in recovery before this build kept only the
      // failure code; it enters the same recovery as any other path.
      const failureCode = entry.failure?.code;
      const failureReason = failureCode === 'ticket_version_conflict'
        ? 'conflict'
        : failureCode === 'save_outcome_unknown'
          ? 'uncertain'
          : failureCode === 'saved_reload_required'
            ? 'committed'
            : null;
      if (failureReason) enterRecoveryReason(failureReason);
    }).then(() => {
      if (!cancelled && isCurrentAuthEpoch(formSessionEpochRef.current)) reconciliation.complete = true;
    }).catch(() => {
      if (cancelled || !isCurrentAuthEpoch(formSessionEpochRef.current)) return;
      // The queue reader already falls back to mirrors/memory when IDB fails.
      // An unexpected reconciliation failure must not be treated as an empty queue.
      setSaveStatusValue('error');
      showToast('Could not load this ticket’s unsent edits. Reopen the ticket before saving.', 'warning');
    });
    return () => {
      cancelled = true;
    };
  }, [enterRecoveryReason, incorporateAdmission, isQboLinked, recoveringRef, setSaveStatusValue, recoveryOwner, ticketId, explicitRecovery]);

  // When the global replay successfully flushes THIS ticket's queued save, flip
  // the pill back to saved and allow the next refetch to hydrate server truth.
  useEffect(() => {
    if (!ticketId) return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ ticketId?: string }>).detail;
      if (detail?.ticketId !== ticketId) return;
      // The replay flushed one admission. If the live form now holds NEWER edits — dirty, or a save
      // in-flight — do NOT clear isDirty or null lastSyncedRef: doing so would
      // unblock the load-sync effect to re-hydrate stale server data over the
      // newer in-memory edit (data loss). Leave the pill as-is and let the
      // form's own save loop persist the newer edits. Only flip to 'saved' and
      // allow re-hydration when the form is clean. Mirrors the on-open guard.
      if (isDirtyRef.current || saveStatusRef.current === 'saving') return;
      // Force the load-sync effect to re-hydrate from the replayed server row.
      lastSyncedRef.current = null;
      setSaveStatusValue('saved');
    };
    window.addEventListener(OFFLINE_SAVE_REPLAYED_EVENT, handler);
    return () => window.removeEventListener(OFFLINE_SAVE_REPLAYED_EVENT, handler);
  }, [setSaveStatusValue, ticketId]);

  // Ref to the latest handleSave closure, populated after handleSave is
  // defined below. Using a ref avoids stale closures inside debounce timers.
  const handleSaveRef = useRef<() => void>(() => {});

  // Kick off the 1s debounced save. Shared by field/labor/material changes
  // and by explicit blur. The backend coalescer (scheduleTrelloSync) ensures
  // rapid PUTs collapse into a single Trello updateComment call.
  const scheduleDebouncedSave = useCallback(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (isDirtyRef.current && !recoveringRef.current) handleSaveRef.current();
    }, 1000);
  }, []);

  const handleFieldChange = useCallback((patch: Partial<UpdateTicketData>) => {
    if (!canEditSession() || recoveringRef.current) return;
    setFieldErrors((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(patch) as Array<keyof TicketFieldErrors>) {
        delete next[key];
      }
      if (patch.customer_id !== undefined) delete next.customer_id;
      return next;
    });
    const previousFormState = draftSnapshotRef.current.formState;
    const nextFormState = { ...previousFormState, ...patch };
    // Selecting a customer no longer auto-fills the requisitioner. It stays
    // blank until someone types it; copying the customer name here was wrong
    // (the requisitioner is rarely the same as the bill-to customer).
    draftSnapshotRef.current = {
      ...draftSnapshotRef.current,
      formState: nextFormState,
    };
    setFormState(nextFormState);
    markDirty();
    scheduleDebouncedSave();
  }, [canEditSession, markDirty, scheduleDebouncedSave]);

  const normalizeAndGeocodeCoordinates = useCallback(async () => {
    const generation = formGenerationRef.current;
    const currentFormState = draftSnapshotRef.current.formState;
    const coordinates = currentFormState.coordinates?.trim();
    if (!canEditSession() || !coordinates || isLocked || recoveringRef.current) return;
    const lastCoordinates = lastGeocodedCoordinatesRef.current;
    if (lastCoordinates?.raw === coordinates || lastCoordinates?.normalized === coordinates) return;

    // F-014: valid decimal, hemisphere, DDM, and DMS input is normalized on
    // blur; partial or ambiguous input is left untouched and never geocoded.
    const parsed = parseCoordinatesText(coordinates);
    if (!parsed) return;

    lastGeocodedCoordinatesRef.current = {
      raw: coordinates,
      normalized: parsed.normalized,
    };

    if (parsed.normalized !== coordinates) {
      const normalizedFormState = {
        ...currentFormState,
        coordinates: parsed.normalized,
      };
      draftSnapshotRef.current = {
        ...draftSnapshotRef.current,
        formState: normalizedFormState,
      };
      setFormState(normalizedFormState);
      markDirty();
      scheduleDebouncedSave();
    }

    if (currentFormState.state && currentFormState.county) return;

    try {
      const result = await geocoding.geocode(parsed.normalized);
      if (!canEditSession() || formGenerationRef.current !== generation || recoveringRef.current) return;
      const latestFormState = draftSnapshotRef.current.formState;
      const nextFormState = {
        ...latestFormState,
        ...mergeGeocodeResult(latestFormState, result),
      };
      draftSnapshotRef.current = {
        ...draftSnapshotRef.current,
        formState: nextFormState,
      };
      setFormState(nextFormState);
      markDirty();
      scheduleDebouncedSave();
    } catch {
      // Keep the normalized coordinate even when location lookup is unavailable.
    }
  }, [
    isLocked,
    markDirty,
    scheduleDebouncedSave,
  ]);

  const handleLaborChange = useCallback((entries: LaborEntry[]) => {
    if (!canEditSession() || recoveringRef.current) return;
    setFieldErrors((prev) => {
      // Drop the section-level error AND any per-row errors, since the user
      // is actively editing — the next autosave will re-validate.
      const next = { ...prev, labor_entries: undefined };
      delete next.labor_rows;
      return next;
    });
    draftSnapshotRef.current = {
      ...draftSnapshotRef.current,
      laborEntries: entries,
    };
    setLaborEntries(entries);
    markDirty();
    scheduleDebouncedSave();
  }, [canEditSession, markDirty, scheduleDebouncedSave]);

  const handleMembersChange = useCallback((ids: string[]) => {
    if (!canEditSession() || recoveringRef.current) return;
    const nextMemberIds = [...new Set(ids.filter(Boolean))];
    draftSnapshotRef.current = {
      ...draftSnapshotRef.current,
      memberIds: nextMemberIds,
    };
    setMemberIds(nextMemberIds);
    markDirty();
    scheduleDebouncedSave();
  }, [canEditSession, markDirty, scheduleDebouncedSave]);

  const handleMaterialChange = useCallback((entries: MaterialEntry[]) => {
    if (!canEditSession() || recoveringRef.current) return;
    setFieldErrors((prev) => {
      const next = { ...prev, material_entries: undefined };
      delete next.material_rows;
      return next;
    });
    draftSnapshotRef.current = {
      ...draftSnapshotRef.current,
      materialEntries: entries,
    };
    setMaterialEntries(entries);
    markDirty();
    scheduleDebouncedSave();
  }, [canEditSession, markDirty, scheduleDebouncedSave]);

  const handleEquipmentChange = useCallback((entries: EquipmentEntry[]) => {
    if (!canEditSession() || recoveringRef.current) return;
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.equipment_rows;
      return next;
    });
    draftSnapshotRef.current = {
      ...draftSnapshotRef.current,
      equipmentEntries: entries,
    };
    setEquipmentEntries(entries);
    markDirty();
    scheduleDebouncedSave();
  }, [canEditSession, markDirty, scheduleDebouncedSave]);

  // Autosave is serialized: each PUT sends a full ticket snapshot, so allowing
  // concurrent PUTs lets an older request land after a newer one and revert the
  // row. This queue keeps at most one request in flight and re-runs with the
  // latest draft if edits arrived while the current request was saving.
  const saveLoopPromiseRef = useRef<Promise<void> | null>(null);
  const saveRequestedRef = useRef(false);
  // Settles the CURRENTLY in-flight dispatchSave promise. React Query drops the
  // mutation observer on unmount, so a save in flight at unmount never has its
  // onSuccess/onError fire — its promise would never resolve and the shared
  // per-ticket lock it holds would stay held for the page's lifetime, wedging
  // the offline replay subsystem. The unmount handler calls this to release the
  // lock. Cleared whenever a save settles normally.
  const pendingSaveSettleRef = useRef<(() => void) | null>(null);

  const dispatchSave = useCallback((saveVersion: number, lease: TicketSaveLease): Promise<void> => {
    if (!ticketId || !canEditSession() || !queueReconciliationRef.current.complete) return Promise.resolve();

    setSaveStatusValue('saving');
    // Queue writers below persist this built payload directly, so scoping here
    // also scopes offline replay instead of relying on backend drop tolerance.
    const data = withVersionToken(buildCurrentUpdatePayload(), ticketVersionRef.current);
    const saveBaseSnapshot = ticketBaseSnapshotRef.current;
    const owner = draftOwnerRef.current;
    const epoch = formSessionEpochRef.current;
    const generation = formGenerationRef.current;
    if (!owner) return Promise.resolve();
    const admission = createTicketSaveSnapshot(owner, ticketId, data, saveBaseSnapshot, undefined, undefined,
      [...incorporatedAdmissionsRef.current.values()]);
    incorporateAdmission(admission);
    saveContextRef.current = { lease, epoch, owner };
    const abortController = new AbortController();
    inflightAbortRef.current = abortController;

    return new Promise<void>((resolve) => {
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        // Clear the held-save handle so a later unmount doesn't try to release
        // an already-settled save (and so the ref only ever points at a save
        // that is genuinely still in flight).
        if (pendingSaveSettleRef.current === settle) pendingSaveSettleRef.current = null;
        if (inflightAbortRef.current === abortController) inflightAbortRef.current = null;
        if (saveContextRef.current?.lease === lease) saveContextRef.current = null;
        resolve();
      };
      // Publish this save's settle so the unmount handler can release the
      // shared per-ticket lock if the mutation is still in flight at teardown.
      pendingSaveSettleRef.current = settle;
      const isCurrentSave = () => draftVersionRef.current === saveVersion;
      const isActiveSave = () => !settled && !abortController.signal.aborted &&
        formGenerationRef.current === generation;

      const handleAttemptSuccess = (updatedTicket: Ticket, sent: UpdateTicketData) => {
          if (!isActiveSave() || !isCurrentAuthEpoch(epoch)) {
            settle();
            return;
          }
          // Hand the exact version returned by save A directly to queued save B.
          // React effects/cache propagation are asynchronous; relying on them
          // leaves a window where B can reuse A's now-stale request token.
          const savedToken = ticketVersionToken(updatedTicket);
          if (Object.keys(savedToken).length > 0) {
            ticketVersionRef.current = savedToken;
            ticketBaseSnapshotRef.current = createTicketConflictSnapshot(updatedTicket);
            // The server now holds exactly what was sent, carried fields too.
            ticketBasePayloadRef.current = sent as Record<string, unknown>;
          }
          const currentSave = isCurrentSave();
          isDirtyRef.current = !currentSave;
          setIsDirty(!currentSave);
          setSaveStatusValue(currentSave ? 'saved' : 'dirty');
          // Successful save resets the error streak so the next failure
          // shows a fresh toast.
          errorStreakRef.current = false;
          if (currentSave) {
            // The server has everything this form held: its kept-on-leave
            // copy is done. Not when newer edits arrived meanwhile (#1041).
            removeTicketRetainedDraft(owner, ticketId, retainSlotRef.current);
            // Allow the sync effect to pick up the server response.
            lastSyncedRef.current = null;
            // Clear any per-row errors left over from a prior failed save.
            setFieldErrors((prev) => {
              if (!prev.labor_rows && !prev.material_rows && !prev.equipment_rows) return prev;
              const next = { ...prev };
              delete next.labor_rows;
              delete next.material_rows;
              delete next.equipment_rows;
              return next;
            });
            clearTimeout(savedFlashRef.current);
            savedFlashRef.current = setTimeout(() => {
              if (!isDirtyRef.current && draftVersionRef.current === saveVersion) {
                setSaveStatusValue('clean');
              }
            }, 2000);
          }
          if (currentSave && updatedTicket?.trello_sync_failed) {
            showToast('Saved locally — Trello sync pending', 'info');
          }
          // Even a current draft can acknowledge only identities it incorporated,
          // never every earlier sequence in this tab. Capture provenance at dispatch.
          const incorporated = currentSave ? admission.incorporatedAdmissions ?? [] : [];
          for (const identity of [admission, ...incorporated]) incorporatedAdmissionsRef.current.delete(ticketSaveEntryId(identity));
          void deleteTicketSave(admission, incorporated).then(settle, settle);
      };

      // The draft changed after this attempt was built and the server refused
      // the attempt's version. The attempt is never resent: the next save
      // sends the current draft. So that save does not repeat the conflict,
      // check what moved since this save's base. Only system-owned fields (the
      // geocoder's state/county): take the server's version and carry
      // state/county into the draft unless the user edited them. Anything
      // else: the same conflict recovery as a current draft.
      const adoptSystemChangesForCurrentDraft = (currentTicket: Ticket) => {
        // Another path moved the form's version meanwhile: leave it to that.
        if (ticketBaseSnapshotRef.current === saveBaseSnapshot) {
          const rebased = rebaseTicketSaveForSystemChanges(
            buildCurrentUpdatePayload(),
            saveBaseSnapshot,
            currentTicket,
          );
          if (!rebased) {
            const changedFields = saveBaseSnapshot
              ? changedTicketFieldsSinceBase(saveBaseSnapshot, currentTicket)
              : [];
            enterRecoveryReason('conflict', changedFields);
            settle();
            return;
          }
          ticketVersionRef.current = ticketVersionToken(currentTicket);
          ticketBaseSnapshotRef.current = createTicketConflictSnapshot(currentTicket);
          // The form was not reloaded onto that version: its base payload is unknown.
          ticketBasePayloadRef.current = undefined;
          const systemPatch = { state: rebased.state, county: rebased.county };
          draftSnapshotRef.current = {
            ...draftSnapshotRef.current,
            formState: { ...draftSnapshotRef.current.formState, ...systemPatch },
          };
          setFormState((current) => ({ ...current, ...systemPatch }));
        }
        isDirtyRef.current = true;
        setIsDirty(true);
        setSaveStatusValue('dirty');
        settle();
      };

      // A conflict's system rebase decides only from a confirmed network read
      // (the read Reload uses), never refetch(): a failed refetch resolves
      // with the cached ticket, and the service worker may answer from its
      // cache, so either can be the very version the server just refused.
      // The read must be strictly newer than the refused version; a failed
      // read or one that did not advance is a conflict (no adoption, no
      // resend). Nothing is touched once the session or form moved on.
      const readNewerTicketForRebase = (
        refused: UpdateTicketData,
        onNewer: (currentTicket: Ticket) => void,
      ) => {
        const isStillHere = () => isActiveSave() && isCurrentAuthEpoch(epoch);
        const conflict = () => {
          enterRecoveryReason('conflict');
          settle();
        };
        tickets.getFresh(ticketId).then((fresh) => {
          if (!isStillHere()) {
            settle();
            return;
          }
          // Refused (null) once the session moved; never below a newer copy.
          const currentTicket = setTicketDetail(queryClient, ticketId, fresh, epoch);
          if (!currentTicket) {
            settle();
            return;
          }
          if (!isNewerThanVersionToken(currentTicket, payloadVersionToken(refused))) {
            conflict();
            return;
          }
          onNewer(currentTicket);
        }, (readError: unknown) => {
          if (!isStillHere() || isStaleSession(readError)) {
            settle();
            return;
          }
          conflict();
        });
      };

      const handleAttemptError = (
        err: unknown,
        attemptData: UpdateTicketData,
        allowSystemRebase: boolean,
      ) => {
          if (!isActiveSave()) {
            settle();
            return;
          }
          // Signed out or another user signed in since the save started:
          // nothing of this answer reaches the form, the drafts or the queue.
          if (isStaleSession(err) || !isCurrentAuthEpoch(epoch)) {
            settle();
            return;
          }
          if (isActorMismatch(err)) {
            actorMismatchRef.current = true;
            manualRetryRequiredRef.current = true;
            isDirtyRef.current = true;
            setIsDirty(true);
            setSaveStatusValue('error');
            snapshotTicketSave(owner, ticketId,
              withVersionToken(buildCurrentUpdatePayload(), ticketVersionRef.current), saveBaseSnapshot,
              { failedAt: Date.now(), code: 'actor_mismatch', status: 409,
                message: 'The signed-in user changed. Sign in as the edit owner and reopen this ticket.' });
            showToast('The signed-in user changed. Your edit is kept for its owner; sign in again before saving.', 'error');
            settle();
            return;
          }
          // A recovery draft (another tab, the replay) appeared while the save
          // waited: nothing was sent. Enter that recovery, keeping these edits.
          if (isTicketPutBlocked(err)) {
            isRecoveryBlocked();
            settle();
            return;
          }

          // The save may have committed (or did). Whatever the draft
          // generation, stop every save path until a confirmed read of the
          // server's ticket: never retried, queued or offered as an overwrite.
          const recoveryReason = ticketRecoveryReason(err);
          if (recoveryReason === 'uncertain' || recoveryReason === 'committed') {
            enterRecoveryReason(recoveryReason);
            settle();
            return;
          }

          if (!isCurrentSave()) {
            if (allowSystemRebase && recoveryReason === 'conflict') {
              readNewerTicketForRebase(attemptData, adoptSystemChangesForCurrentDraft);
              return;
            }
            isDirtyRef.current = true;
            setIsDirty(true);
            setSaveStatusValue('dirty');
            settle();
            return;
          }

          if (allowSystemRebase && isTicketVersionConflict(err)) {
            readNewerTicketForRebase(attemptData, (currentTicket) => {
              // The tech may have edited/backgrounded during the read.
              // Leave that draft to its own save instead of retrying old content.
              if (!isCurrentSave()) {
                adoptSystemChangesForCurrentDraft(currentTicket);
                return;
              }
              const rebased = rebaseTicketSaveForSystemChanges(
                attemptData,
                saveBaseSnapshot,
                currentTicket,
              );
              if (!rebased) {
                const changedFields = saveBaseSnapshot
                  ? changedTicketFieldsSinceBase(saveBaseSnapshot, currentTicket)
                  : [];
                enterRecoveryReason('conflict', changedFields);
                settle();
                return;
              }

              ticketVersionRef.current = ticketVersionToken(currentTicket);
              ticketBaseSnapshotRef.current = createTicketConflictSnapshot(currentTicket);
              ticketBasePayloadRef.current = undefined;
              const systemPatch = { state: rebased.state, county: rebased.county };
              draftSnapshotRef.current = {
                ...draftSnapshotRef.current,
                formState: { ...draftSnapshotRef.current.formState, ...systemPatch },
              };
              setFormState((current) => ({ ...current, ...systemPatch }));
              // Retry once under the original immutable admission identity.
              // A second conflict enters recovery rather than moving the token.
              updateTicket.mutate(rebased, {
                onSuccess: (updated) => handleAttemptSuccess(updated, rebased),
                onError: (retryError) => handleAttemptError(retryError, rebased, false),
              });
            });
            return;
          }

          if (isTicketVersionConflict(err)) {
            enterRecoveryReason('conflict');
            settle();
            return;
          }

          // Offline / network failure (offline, raw fetch TypeError, or a
          // timeout normalized to status 0): never show a hard error and never
          // lose the edit. Queue the latest snapshot (last-write-wins per
          // ticket) and reflect it as "Queued offline" — the global replay
          // (useOfflineQueueReplay) PUTs it when connectivity returns. A 4xx is
          // NOT offline (we reached the server) and falls through below.
          if (isOfflineError(err) && !isQboLinked) {
            isDirtyRef.current = true;
            setIsDirty(true);
            // Synchronous localStorage mirror FIRST (mirrors the unmount flush /
            // persistOnHide handlers): the durable snapshot must exist the instant
            // the gateway error is observed, closing the iOS freeze/evict-before-
            // IDB-commit window. Then enqueue and only THEN release the per-ticket
            // lock (settle), so a concurrent replay re-reads the freshly queued
            // payload instead of racing the IndexedDB write.
            const mirrored = persistTicketSaveSync(owner, ticketId, attemptData, saveBaseSnapshot, undefined, admission);
            const reportStored = (stored: boolean) => {
              if (isActiveSave() && isCurrentAuthEpoch(epoch)) {
                setSaveStatusValue(stored ? (isCurrentSave() ? 'queued' : 'dirty') : 'error');
                if (!stored) showToast('Your edit could not be stored offline. Keep this ticket open and sign in again before saving.', 'error');
              }
              settle();
            };
            void enqueueTicketSave(owner, ticketId, attemptData, saveBaseSnapshot, undefined, admission).then(
              (stored) => reportStored(stored),
              () => reportStored(mirrored),
            );
            return;
          }

          isDirtyRef.current = true;
          setIsDirty(true);
          setSaveStatusValue('error');

          // Parse the server's structured `validation_failed` payload so we
          // can pinpoint the offending row/field instead of just toasting.
          // Backend shape: { error: 'validation_failed', details: [...] }
          // (see backend/src/routes/tickets.js + utils/request-validation.js).
          const apiError = err as { status?: number; data?: { error?: string; details?: unknown }; message?: string };
          const isValidationFailure =
            apiError?.status === 400 &&
            apiError?.data &&
            typeof apiError.data === 'object' &&
            (apiError.data as { error?: string }).error === 'validation_failed' &&
            Array.isArray((apiError.data as { details?: unknown }).details);

          if (isValidationFailure) {
            const details = (apiError.data as { details: Array<{ path?: string; field?: string; message?: string }> }).details;
            const rowErrors = parseValidationDetails(details);
            setFieldErrors((prev) => ({
              ...prev,
              labor_rows: rowErrors.labor_rows,
              material_rows: rowErrors.material_rows,
              equipment_rows: rowErrors.equipment_rows,
            }));
          }

          // #952: Ayrical froze the AFE/OpenInvoice references once a receipt
          // exists. Point at the fields the server names (only those this
          // save changed); the server message says why.
          const lockedBody = apiError?.data as { error?: string; fields?: unknown } | undefined;
          if (apiError?.status === 409 && lockedBody?.error === 'openinvoice_ticket_reference_locked') {
            const lockedFields = Array.isArray(lockedBody.fields)
              ? lockedBody.fields.filter((field): field is string => typeof field === 'string')
              : [];
            if (lockedFields.length > 0) {
              setFieldErrors((prev) => ({
                ...prev,
                ...Object.fromEntries(lockedFields.map((field) => [
                  field,
                  apiError.message || 'This field is locked because a receipt exists in OpenInvoice.',
                ])),
              }));
            }
          }

          // Toast on the first failure of a streak; subsequent failures
          // update the badge silently to avoid spamming the user as they type.
          if (!errorStreakRef.current) {
            errorStreakRef.current = true;
            const message = isValidationFailure
              ? 'Some fields need attention before saving'
              : (apiError?.message || (err instanceof Error ? err.message : 'Failed to save ticket'));
            showToast(message, 'error');
          }
          // Resolve (not reject) so flushPendingSave callers don't unhandled-
          // reject. Errors are surfaced via the toast above + saveStatus='error',
          // and handleTransition checks saveStatus before proceeding.
          settle();
      };

      updateTicket.mutate(data, {
        onSuccess: (updated) => handleAttemptSuccess(updated, data),
        onError: (err) => handleAttemptError(err, data, true),
      });
    });
  }, [buildCurrentUpdatePayload, enterRecoveryReason, incorporateAdmission, isQboLinked, isRecoveryBlocked, queryClient, recoveringRef, setSaveStatusValue, ticketId, updateTicket]);

  const requestSave = useCallback((): Promise<void> | undefined => {
    if (!ticketId || !canEditSession()) return undefined;
    if (manualRetryRequiredRef.current) return undefined;
    // Reload required: nothing may send the draft until a confirmed reload.
    if (recoveringRef.current) return saveLoopPromiseRef.current ?? undefined;
    const reconciliation = queueReconciliationRef.current;
    if (reconciliation.ticketId !== ticketId || !reconciliation.complete) {
      return reconciliation.promise?.then(async () => {
        if (queueReconciliationRef.current === reconciliation && reconciliation.complete) await requestSave();
      });
    }

    saveRequestedRef.current = true;
    if (saveLoopPromiseRef.current) {
      return saveLoopPromiseRef.current;
    }

    const generation = formGenerationRef.current;
    const runLoop = async () => {
      while (formGenerationRef.current === generation && saveRequestedRef.current && !recoveringRef.current) {
        saveRequestedRef.current = false;
        const saveVersion = draftVersionRef.current;
        // Run the PUT under the shared per-ticket lock so this autosave and the
        // offline-queue replay can never PUT the same ticket concurrently — a
        // replay landing after a newer save would silently revert the row. The
        // lock holds until dispatchSave settles (after its queue clear/enqueue
        // commits), so a waiting replay re-reads up-to-date queue state.
        // Recovery is checked again inside the lock, right before the request:
        // the ticket may have entered it (an answer, the replay, another tab)
        // while this save waited.
        await withTicketSaveLock(ticketId, (lease) => (
          formGenerationRef.current !== generation || isRecoveryBlocked()
            ? Promise.resolve() : dispatchSave(saveVersion, lease)
        ));
      }
    };

    saveLoopPromiseRef.current = runLoop().finally(() => {
      if (formGenerationRef.current !== generation) return;
      saveLoopPromiseRef.current = null;
      if (saveRequestedRef.current) {
        return requestSave();
      }
    });

    return saveLoopPromiseRef.current;
  }, [dispatchSave, isRecoveryBlocked, ticketId]);

  const handleSave = useCallback(() => {
    requestSave();
  }, [requestSave]);

  // Retry only for an ordinary failed save. There is no "overwrite" for a
  // reload-required state: the draft can hold lines the server already saved
  // under ids the form never received, so resending it could add them twice.
  const handleRetrySave = useCallback(() => {
    if (recoveringRef.current || !canEditSession()) return;
    if (actorMismatchRef.current) {
      // Only an explicit retry after confirming the original author's cookie
      // can release this hold. The PUT still checks the actor on the server.
      const epoch = formSessionEpochRef.current;
      const ticket = formTicketIdRef.current;
      void auth.getMe().then((session) => {
        if (!isCurrentAuthEpoch(epoch) || formTicketIdRef.current !== ticket || recoveringRef.current ||
          ticketRecoveryOwner(session.user) !== draftOwnerRef.current) return;
        actorMismatchRef.current = false;
        manualRetryRequiredRef.current = false;
        requestSave();
      }).catch(() => {
        showToast('Sign in as the edit owner before retrying this save.', 'error');
      });
      return;
    }
    manualRetryRequiredRef.current = false;
    requestSave();
  }, [requestSave]);

  const handleRestoreQuarantine = useCallback((entry: QueuedTicketSave) => {
    if (!canEditSession() || isDirtyRef.current || recoveringRef.current) return;
    if (formTicketIdRef.current !== ticketId || entry.owner !== recoveryOwnerRef.current || entry.ticketId !== ticketId) return;
    setExplicitRecovery(entry);
  }, [ticketId]);

  // Retained drafts (#1041): edits kept on leaving a QuickBooks-linked ticket
  // or by a recovery Reload. Offered only while the form is clean and not
  // recovering, and never this form's own (its edits are still on screen).
  // Nothing sends them: the user restores (the normal autosave follows) or
  // discards them.
  const [, refreshRetained] = useReducer((count: number) => count + 1, 0);
  useEffect(() => {
    if (!ticketId) return undefined;
    const onRetained = (event: Event) => {
      if ((event as CustomEvent<{ ticketId?: string }>).detail?.ticketId === ticketId) refreshRetained();
    };
    const onStorage = (event: StorageEvent) => {
      if (recoveryOwner && isTicketRetainedStorageKey(event.key, recoveryOwner, ticketId)) refreshRetained();
    };
    window.addEventListener(TICKET_RETAINED_EVENT, onRetained);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(TICKET_RETAINED_EVENT, onRetained);
      window.removeEventListener('storage', onStorage);
    };
  }, [recoveryOwner, ticketId]);

  const describeRetainedDraft = useCallback((draft: TicketRetainedDraft, current: Ticket): TicketRetainedDraftView => {
    const sameVersion = sameVersionToken(payloadVersionToken(draft.payload), ticketVersionToken(current));
    const base = draft.baseSnapshot;
    const serverChangedFields = sameVersion || !base
      ? []
      : changedTicketFieldsSinceBase(base, current).filter((field) => field !== 'updated_at');
    // Compare with what the form held for the base version (same shaping and
    // role scoping), so untouched or carried fields never count as edits.
    // A draft from a path with no form falls back to its conflict snapshot.
    const basePayload = draft.basePayload ?? (base
      ? buildUpdatePayload(snapshotFromTicket(ticketFromConflictSnapshot(base), []), canEditOfficeFields, canViewRates)
      : undefined);
    const editedFields = editedTicketFieldsSinceBase(draft.payload, basePayload as Record<string, unknown> | undefined);
    const currentPayload = buildUpdatePayload(snapshotFromTicket(current, []), canEditOfficeFields, canViewRates);
    const differsFromCurrent = new Set(editedTicketFieldsSinceBase(draft.payload, currentPayload as Record<string, unknown>));
    // Only fields the ticket is known to hold can count as already saved; any
    // other edited field is always reapplied, never dropped.
    const alreadySavedFields = sameVersion
      ? []
      : editedFields.filter((field) => isTicketConflictField(field) && !differsFromCurrent.has(field));
    return {
      slot: draft.slot,
      fromThisTab: draft.tabId === TICKET_RECOVERY_TAB_ID,
      fromReload: Boolean(draft.recoveryReason),
      createdAt: draft.createdAt,
      summary: draft.summary,
      sameVersion,
      comparable: sameVersion || Boolean(base),
      canRestore: sameVersion || (Boolean(base) && editedFields.length > alreadySavedFields.length),
      serverChangedFields,
      editedFields,
      alreadySavedFields,
      // A field the conflict snapshot lacks may have changed on the server
      // unseen: reapplying it always needs the user's confirmation.
      overlappingFields: sameVersion ? [] : editedFields.filter((field) => !alreadySavedFields.includes(field) &&
        (serverChangedFields.includes(field) || !isTicketConflictField(field))),
    };
  }, [canEditOfficeFields, canViewRates]);

  const retainedDrafts: TicketRetainedDraftView[] =
    ticket && ticket.id === ticketId && !isDirty && !reloadRequired && recoveryOwner &&
    isCurrentAuthEpoch(formSessionEpochRef.current)
      ? listTicketRetainedDrafts(recoveryOwner, ticketId)
        .filter((draft) => draft.slot !== retainSlotRef.current)
        .map((draft) => describeRetainedDraft(draft, ticket))
      : [];

  /**
   * Puts a retained draft back in the form. Unchanged ticket: the edits as
   * they were. Moved ticket: only the fields these edits changed, over the
   * current ticket and pinned to its version; when the server changed one of
   * them too, only with `overwrite` (the user confirmed it). The form is then
   * dirty and the normal autosave and QuickBooks edit rules apply.
   */
  const restoreRetainedDraft = useCallback((slot: string, options: { overwrite?: boolean } = {}): boolean => {
    const owner = recoveryOwnerRef.current;
    const current = ticket;
    if (!owner || !current || current.id !== ticketId || formTicketIdRef.current !== ticketId) return false;
    if (!canEditSession() || isLocked || recoveringRef.current || isDirtyRef.current || saveStatusRef.current === 'saving') return false;
    const draft = listTicketRetainedDrafts(owner, ticketId).find((candidate) => candidate.slot === slot);
    if (!draft || draft.slot === retainSlotRef.current) return false;
    const view = describeRetainedDraft(draft, current);
    if (!view.canRestore) return false;
    if (!view.sameVersion && view.overlappingFields.length > 0 && !options.overwrite) return false;
    const restored = snapshotFromRetainedPayload(draft.payload);
    let next = restored;
    if (!view.sameVersion) {
      next = snapshotFromTicket(current, draftSnapshotRef.current.materialEntries);
      const formState = { ...next.formState } as Record<string, unknown>;
      for (const field of view.editedFields) {
        if (view.alreadySavedFields.includes(field)) continue;
        if (field === 'labor_entries') next.laborEntries = restored.laborEntries;
        else if (field === 'member_ids') next.memberIds = restored.memberIds;
        else if (field === 'material_entries') next.materialEntries = restored.materialEntries;
        else if (field === 'equipment') next.equipmentEntries = restored.equipmentEntries;
        else formState[field] = (restored.formState as Record<string, unknown>)[field];
      }
      next = { ...next, formState: formState as UpdateTicketData };
    }
    draftOwnerRef.current = owner;
    ticketVersionRef.current = ticketVersionToken(current);
    ticketBaseSnapshotRef.current = createTicketConflictSnapshot(current);
    ticketBasePayloadRef.current = formPayloadOf(snapshotFromTicket(current, draftSnapshotRef.current.materialEntries));
    lastSyncedRef.current = ticketSyncKey(current);
    draftSnapshotRef.current = next;
    setFormState(next.formState);
    setLaborEntries(next.laborEntries);
    setMemberIds(next.memberIds);
    setMaterialEntries(next.materialEntries);
    setEquipmentEntries(next.equipmentEntries);
    setFieldErrors({});
    // The form holds the edits now; its own hide/close keeps them from here.
    removeTicketRetainedDraft(owner, ticketId, draft.slot, draft.createdAt);
    markDirty();
    scheduleDebouncedSave();
    return true;
  }, [describeRetainedDraft, isLocked, markDirty, scheduleDebouncedSave, ticket, ticketId]);

  const discardRetainedDraft = useCallback((slot: string): boolean => {
    const owner = recoveryOwnerRef.current;
    if (!owner || !isCurrentAuthEpoch(formSessionEpochRef.current) || formTicketIdRef.current !== ticketId) return false;
    const draft = listTicketRetainedDrafts(owner, ticketId).find((candidate) => candidate.slot === slot);
    if (!draft) return false;
    removeTicketRetainedDraft(owner, ticketId, draft.slot, draft.createdAt);
    return true;
  }, [ticketId]);

  // The recovery state machine's exit: a confirmed network read.
  const handleReloadLatest = recoveryApi.reload;

  // Keep the ref current so scheduleDebouncedSave and flushPendingSave
  // always call the latest handleSave closure.
  handleSaveRef.current = handleSave;

  const handleBlurSave = useCallback((field?: 'coordinates') => {
    if (!canEditSession()) return;
    if (field === 'coordinates') {
      void normalizeAndGeocodeCoordinates();
    }
    scheduleDebouncedSave();
  }, [canEditSession, normalizeAndGeocodeCoordinates, scheduleDebouncedSave]);

  // Flush any pending debounced save and await the in-flight mutation.
  //
  // Public signature kept stable: `() => Promise<void> | undefined` so callers
  // (TicketModal, MobileTicketView, ticket transition flows) don't break.
  // Returns:
  //   - undefined when there is nothing dirty AND no save in flight
  //   - Promise<void> that resolves once the save round-trip settles, so
  //     transitions / navigations / panel closes can rely on the persisted
  //     row matching the local form state. Previously this returned
  //     `Promise.resolve()` immediately, causing transitions to hit the
  //     backend before the autosave PUT completed.
  const flushPendingSave = useCallback((): Promise<void> | undefined => {
    clearTimeout(debounceRef.current);
    debounceRef.current = undefined;
    const reconciliation = queueReconciliationRef.current;
    if (!reconciliation.complete) {
      return reconciliation.promise?.then(async () => {
        if (queueReconciliationRef.current === reconciliation && reconciliation.complete) await flushPendingSave();
      });
    }
    if (isDirtyRef.current) {
      return requestSave();
    }
    // Either we just kicked one off (above) or a save was already in flight
    // when the caller invoked us. Either way, await it.
    if (saveLoopPromiseRef.current) {
      return saveLoopPromiseRef.current;
    }
    return undefined;
  }, [requestSave]);

  const persistCapturedSnapshot = useCallback((updateForm = false, snapshot?: QueuedTicketSave): Promise<boolean> => {
    if (!isDirtyRef.current || !ticketId) return Promise.resolve(true);
    const owner = draftOwnerRef.current;
    const epoch = formSessionEpochRef.current;
    const generation = formGenerationRef.current;
    const payload = withVersionToken(buildCurrentUpdatePayload(), ticketVersionRef.current);
    const baseSnapshot = ticketBaseSnapshotRef.current;
    let pending: Promise<boolean>;
    if (owner && recoveringRef.current) {
      const previous = readTicketRecoveryDraft(owner, ticketId);
      pending = Promise.resolve(writeTicketRecoveryDraft(buildTicketRecoveryDraft(owner, ticketId, previous?.reason ?? 'conflict', payload, baseSnapshot,
        undefined, ticketBasePayloadRef.current)));
    } else if (owner && isQboLinked) {
      // QuickBooks-linked edits never enter the offline queue. Hiding or
      // leaving the ticket is not a conflict (#1041): keep a retained draft,
      // which blocks nothing; the autosave carries on when the user returns.
      pending = Promise.resolve(retainOwnEdits(owner, ticketId, payload, baseSnapshot).durable);
    } else {
      const failure = manualRetryRequiredRef.current ? { failedAt: Date.now(), code: actorMismatchRef.current ? 'actor_mismatch' : 'legacy_manual_retry',
          message: 'Review this edit and retry saving manually as its owner.' } : undefined;
      if (!snapshot && owner && isTicketQueueTabReady()) {
        snapshot = createTicketSaveSnapshot(owner, ticketId, payload, baseSnapshot, failure, undefined,
          [...incorporatedAdmissionsRef.current.values()]);
      }
      if (snapshot) incorporateAdmission(snapshot);
      pending = snapshot
        ? snapshotTicketSave(snapshot.owner, snapshot.ticketId, snapshot.payload, snapshot.baseSnapshot, failure, snapshot.updatedAt, snapshot)
        : snapshotTicketSave(owner, ticketId, payload, baseSnapshot, failure);
    }
    return pending.then((durable) => {
      if (!durable && isCurrentAuthEpoch(epoch) && formGenerationRef.current === generation) {
        if (updateForm) setSaveStatusValue('error');
        showToast('This browser could not store your edits. They are kept only in this tab; copy them before closing or reloading it.', 'warning');
      }
      return durable;
    });
  }, [buildCurrentUpdatePayload, incorporateAdmission, isQboLinked, retainOwnEdits, setSaveStatusValue, ticketId]);

  // Flush on unmount so dirty state from the review panel / ticket modal /
  // ticket edit page is never silently dropped when the component tears down
  // inside the 1000ms debounce window.
  //
  // The "Rusty" incident: the normal autosave path runs its PUT through the
  // React Query mutation observer, whose per-call onError is NOT invoked once
  // the component unmounts. So a teardown-time failure — classically a fast
  // gateway 502/503/504 from the backend bouncing during a deploy — fired the
  // PUT and then SILENTLY dropped the edit when it failed, because the offline-
  // queue enqueue lives inside that observer callback. Here we instead run the
  // final flush as a detached, observer-free PUT under the shared per-ticket
  // lock, and on an offline/gateway failure we ENQUEUE the snapshot so the
  // offline replay persists it once the backend is back — the tech's last edit
  // is never lost. A reached-server rejection stays blocked for manual recovery.
  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      clearTimeout(savedFlashRef.current);
      if (!canEditSession() || queueReconciliationRef.current.ticketId !== ticketId || !queueReconciliationRef.current.complete) {
        void persistCapturedSnapshot();
        // React drops mutation callbacks on unmount: release the old session's
        // held save without queuing its edits into the new session.
        inflightAbortRef.current?.abort();
        pendingSaveSettleRef.current?.();
        return;
      }
      if (!isDirtyRef.current || !ticketId) return;
      if (manualRetryRequiredRef.current) {
        void persistCapturedSnapshot();
        return;
      }
      // Reload required: the draft must not be sent or queued (see
      // handleRetrySave). An existing durable copy is left as it is.
      if (recoveringRef.current) { void persistCapturedSnapshot(); return; }
      const payload = withVersionToken(buildCurrentUpdatePayload(), ticketVersionRef.current);
      const baseSnapshot = ticketBaseSnapshotRef.current;
      const owner = draftOwnerRef.current;
      const epoch = formSessionEpochRef.current;

      // QBO-linked office corrections never enter the offline replay queue.
      // The edits are kept first as this form's retained draft (#1041), then
      // a best-effort online flush sends them; only its success removes that
      // draft, so switching tickets or going offline loses nothing. It
      // goes through the same gate as every PUT: under the save lock, and
      // never while a recovery draft of the ticket exists (that one is kept,
      // plus this tab's edits as its own draft).
      if (isQboLinked) {
        inflightAbortRef.current?.abort();
        pendingSaveSettleRef.current?.();
        const kept = owner ? retainOwnEdits(owner, ticketId, payload, baseSnapshot).draft : null;
        const forgetKept = () => {
          if (kept) removeTicketRetainedDraft(kept.owner, kept.ticketId, kept.slot, kept.createdAt);
        };
        // Drop the kept copy only once a stored recovery draft holds these
        // exact edits (Reload keeps it). recordTicketRecovery writes nothing
        // when this tab already has a draft, which may hold other edits.
        const toRecovery = (reason: TicketRecoveryReason) => {
          recordTicketRecovery(owner, ticketId, reason, payload, baseSnapshot);
          if (ticketRecoveryDraftHoldsDurably(owner, ticketId, payload)) forgetKept();
        };
        if (typeof navigator === 'undefined' || navigator.onLine) {
          void withTicketSaveLock(ticketId, async (lease) => {
            try {
              await sendTicketPut(lease, payload, { epoch, owner: owner! });
              forgetKept();
            } catch (err) {
              if (isStaleSession(err)) return;
              if (isActorMismatch(err)) {
                toRecovery('conflict');
                return;
              }
              const reason = isTicketPutBlocked(err) ? 'conflict' : ticketRecoveryReason(err);
              if (reason === 'committed') forgetKept();
              else if (reason) toRecovery(reason);
            }
          });
        }
        return;
      }

      // If an autosave is STILL in flight at unmount, its PUT went out through
      // the React Query mutation observer, whose onSuccess/onError is NOT
      // invoked once the component unmounts (a deploy-bounce 504 can hang for
      // seconds). dispatchSave's promise therefore never settles, so the shared
      // per-ticket lock it holds never releases. Chaining the teardown flush
      // behind that dead lock (withTicketSaveLock → prior.then) would queue the
      // detached PUT/enqueue behind a promise that never resolves, so it would
      // NEVER run and the last edit would be lost — the exact "Rusty" trace.
      // Bypass the wedged lock: abort the in-flight PUT, enqueue the latest
      // snapshot directly (last-write-wins), AND settle the held save so the
      // lock is released. The in-flight observer will never settle it, so
      // without this the lock stays held for the rest of the session and the
      // offline replay (which shares it) is stalled for every ticket. Aborting
      // the in-flight PUT before releasing the lock gives replay ownership of
      // CLIENT ordering: no new request can be issued for the old snapshot.
      // Residual (see #501): abort cannot recall a request the server already
      // fully received, so a received-but-stalled old PUT can still commit
      // after the replay — the durable fix is optimistic concurrency on the
      // ticket PUT handler, tracked separately.
      if (saveLoopPromiseRef.current) {
        inflightAbortRef.current?.abort();
        // Synchronous last-ditch mirror first (survives a mobile freeze→discard
        // that an uncommitted async IDB write would not), then the durable
        // enqueue. Both are last-write-wins per ticketId.
        void persistCapturedSnapshot();
        // Release the wedged per-ticket lock: the in-flight mutation's React
        // Query observer won't fire post-unmount, so its dispatchSave promise
        // never settles on its own and the lock would stay held for the rest of
        // the session — silently stalling the offline replay subsystem. Settling
        // it here frees the lock so a later replay drain can run.
        pendingSaveSettleRef.current?.();
        return;
      }

      // No save in flight: durably record the edit FIRST (sync mirror + queue),
      // independent of the per-ticket lock, so the close-time edit survives even
      // if the lock were somehow contended. The detached PUT below then
      // opportunistically supersedes the queue entry on success.
      const snapshot = createTicketSaveSnapshot(owner!, ticketId, payload, baseSnapshot, undefined, undefined,
        [...incorporatedAdmissionsRef.current.values()]);
      const retained = persistCapturedSnapshot(false, snapshot);
      void withTicketSaveLock(ticketId, async (lease) => {
        try {
          // The gate refuses to send while any recovery draft of the ticket
          // exists (the replay, another tab wrote one while this waited).
          await sendTicketPut(lease, payload, { epoch, owner: owner! });
          // A detached form cannot prove it is still current after a reopen.
          // Acknowledge this exact snapshot, leaving other admissions intact.
          await deleteTicketSave(snapshot);
        } catch (err) {
          // Network/gateway failures stay replayable. Reached-server rejections
          // retain a blocked snapshot, so closing never destroys unsent work.
          // A recovery answer enters the same recovery as an open form, with
          // no form: committed means it saved; uncertain or a conflict writes
          // the durable recovery draft, which the replay never sends and the
          // next open of this ticket restores.
          // The session changed (sign-out, another user): drop the answer;
          // the old session's retained drafts must stay untouched.
          if (isStaleSession(err)) return;
          // The ticket entered recovery while this flush waited: keep the
          // edit as this tab's draft next to the stored one.
          if (isTicketPutBlocked(err)) {
            const stored = readTicketRecoveryDraft(owner, ticketId);
            recordTicketRecovery(owner, ticketId, stored?.reason ?? 'conflict', payload, baseSnapshot);
            return;
          }
          const recoveryReason = ticketRecoveryReason(err);
          if (recoveryReason === 'committed') {
            await deleteTicketSave(snapshot);
          } else if (recoveryReason) {
            recordTicketRecovery(owner, ticketId, recoveryReason, payload, baseSnapshot);
          } else if (!isOfflineError(err)) {
            const error = err as { status?: number; message?: string; data?: { code?: string; error?: string; message?: string } };
            await retained;
            await markQueuedTicketSaveFailed(snapshot, {
              failedAt: Date.now(), status: error.status, code: error.data?.code || error.data?.error,
              message: error.data?.message || error.message || 'This save was rejected. Review it before retrying.',
            });
          }
        }
      });
    };
  }, [buildCurrentUpdatePayload, canEditSession, isQboLinked, ticketId, persistCapturedSnapshot, retainOwnEdits]);

  // Auth invokes this synchronously before revoking the author. IDB may
  // finish later; the immutable owner and the synchronous mirror keep the
  // snapshot under that author without sending it with the next cookie.
  useEffect(() => {
    const snapshotBeforeAuth = (event: Event) => {
      if (!isCurrentAuthEpoch(formSessionEpochRef.current)) return;
      const detail = (event as CustomEvent<TicketSessionSnapshot>).detail;
      if (isDirtyRef.current) detail.dirtyCount += 1;
      if (isDirtyRef.current) {
        detail.pending.push(persistCapturedSnapshot());
      }
      if (detail.revoke) {
        clearTimeout(debounceRef.current);
        inflightAbortRef.current?.abort();
        pendingSaveSettleRef.current?.();
      }
    };
    window.addEventListener(TICKET_SESSION_SNAPSHOT_EVENT, snapshotBeforeAuth);
    return () => window.removeEventListener(TICKET_SESSION_SNAPSHOT_EVENT, snapshotBeforeAuth);
  }, [persistCapturedSnapshot]);

  // Mobile-safe persistence on backgrounding. Mobile Safari FREEZES a
  // backgrounded tab: the autosave debounce timer stops, no PUT fires, and React
  // unmount cleanup is NOT guaranteed to run if iOS later evicts the frozen tab
  // to reclaim memory. So an edit made inside the 1s debounce window and then
  // backgrounded would be lost on tab-kill. 'visibilitychange'→hidden / 'pagehide'
  // is the standard mobile-safe persistence point: when the form is dirty,
  // enqueue the latest snapshot to the durable offline queue so the global
  // replay restores it on next load. We do NOT attempt a network PUT here — the
  // tab may be milliseconds from suspension; IndexedDB persistence is the only
  // reliable action. A later successful autosave clears this entry, so it can't
  // become a stale revert. A QuickBooks-linked form keeps a retained draft
  // instead (persistCapturedSnapshot, #1041); hiding never enters recovery.
  useEffect(() => {
    if (!ticketId) return;
    const persistOnHide = () => {
      void persistCapturedSnapshot(true);
    };
    const onVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        persistOnHide();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', persistOnHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', persistOnHide);
    };
  }, [persistCapturedSnapshot, ticketId]);

  useEffect(() => {
    if (!isQboLinked) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      const needsQboSync = ticket?.qbo_sync?.sync_state === 'local_changes' ||
        ticket?.qbo_sync?.sync_state === 'failed';
      if (!isDirtyRef.current && !needsQboSync) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [isQboLinked, ticket?.qbo_sync?.sync_state]);

  const handleTransition = useCallback(
    async (targetStatus: TicketStatus, options?: Pick<TransitionPayload, 'move_trello_card' | 'note'>) => {
      if (!canEditSession()) return;
      if (!ticketId) return;
      // Re-entrancy guard. The Move buttons stay enabled even with unsaved
      // changes (issue #409: disabling them turned a click into a silent
      // no-op — no toast, no request — when the autosave debounce hadn't
      // fired). Because we now flush-then-transition below, the button is
      // clickable during that flush window; this ref prevents a second click
      // from firing a duplicate transition before the first settles.
      if (transitionInFlightRef.current) return;
      if (recoveringRef.current) {
        showToast('Reload the ticket before moving it', 'error');
        return;
      }
      // The session this move belongs to, captured before any await: checked
      // after the flush, right before the request goes out, and on the answer.
      const epoch = getAuthEpoch();
      const generation = formGenerationRef.current;

      const nextErrors = validateTransitionFields(
        ticket?.status,
        targetStatus,
        formState,
        laborEntries,
        materialEntries,
        equipmentEntries,
        ticket?.openinvoice_billing_required,
      );
      if (Object.keys(nextErrors).length > 0) {
        setFieldErrors(nextErrors);
        setTransitionRejectionCount((count) => count + 1);
        showToast(describeMissingTransitionFields(nextErrors), 'error');
        return;
      }

      setFieldErrors({});
      transitionInFlightRef.current = true;

      // Flush any pending autosave before transition. The transition endpoint
      // validates the persisted row, so if a debounced save hasn't fired yet
      // (or is still in flight) we'd transition stale data.
      const flush = flushPendingSave();
      if (flush) {
        await flush;
        if (formGenerationRef.current !== generation) return;
        // The session ended during the flush: the move belongs to it.
        if (!isCurrentAuthEpoch(epoch)) {
          transitionInFlightRef.current = false;
          return;
        }
        // handleSave settles the deferred regardless of outcome; check status
        // and abort the transition if the save errored OR is only queued
        // offline. Advancing workflow state when the edits aren't even
        // persisted to the server would transition stale data. handleSave's
        // own error toast only fires on the FIRST failure of a streak and
        // auto-dismisses, so a later Move tap would otherwise be a silent
        // no-op — toast again so the tech knows why nothing moved.
        if (saveStatusRef.current === 'error') {
          transitionInFlightRef.current = false;
          showToast('Fix the save error first — this ticket has unsaved changes', 'error');
          return;
        }
        if (saveStatusRef.current === 'queued') {
          transitionInFlightRef.current = false;
          showToast('Saved offline — reconnect to move this ticket', 'info');
          return;
        }
      }

      transitionTicket.mutate(
        { targetStatus, ...options, sessionEpoch: epoch },
        {
          onSuccess: (updatedTicket) => {
            if (formGenerationRef.current !== generation) return;
            transitionInFlightRef.current = false;
            if (!isCurrentAuthEpoch(epoch)) return;
            showToast(`Ticket moved to ${targetStatus.replace(/_/g, ' ')}`, 'success');
            // A3: the status advanced but the Trello card list move failed.
            // Tell the reviewer so they can move the card themselves — the
            // backend deliberately doesn't fail the transition over Trello.
            if (updatedTicket?.trello_card_move_failed) {
              const target = updatedTicket.trello_card_move_target;
              showToast(
                `Trello card didn't move${target ? ` to "${target}"` : ''} — move it in Trello.`,
                'info',
              );
            }
          },
          onError: (err) => {
            if (formGenerationRef.current !== generation) return;
            transitionInFlightRef.current = false;
            // An answer after a sign-out or user switch writes no draft.
            if (!isCurrentAuthEpoch(epoch)) return;
            // Issue #243: backend's ApiError is a plain object thrown from the
            // shared request helper, NOT an Error instance, so `err instanceof
            // Error` was always false and the toast fell back to the generic
            // "Failed to transition ticket" — hiding *why* the transition was
            // rejected (e.g. "Charge code is required for invoice"). Surface
            // the real validation message so users can fix the gap themselves
            // instead of being stuck on a flagged ticket with no signal.
            showToast(extractTicketTransitionErrorMessage(err), 'error');
            // 409, 503 save_outcome_unknown, 500 saved_reload_required: the
            // ticket changed, maybe by this very move. Same recovery as a
            // save: sticky, and left only through a confirmed read.
            const reason = ticketRecoveryReason(err);
            if (reason) enterRecoveryReason(reason);
          },
        },
      );
    },
    [ticket?.status, ticketId, formState, laborEntries, materialEntries, equipmentEntries, transitionTicket, flushPendingSave, enterRecoveryReason, recoveringRef],
  );

  // Send this ticket to QBO as a grouped document (estimate by default, invoice
  // when the admin toggle is on). On success the ticket becomes invoiced + locked.
  const handleSendToQbo = useCallback(async () => {
    if (!canEditSession()) return;
    if (!ticketId) return;
    if (recoveringRef.current) {
      showToast('Reload the ticket before sending it to QuickBooks', 'error');
      return;
    }
    // Captured now: an answer after a sign-out or user switch touches no
    // form state and enters no recovery.
    const epoch = getAuthEpoch();
    const generation = formGenerationRef.current;
    const isCurrentSend = () => formGenerationRef.current === generation && isCurrentAuthEpoch(epoch);
    setQboPushError(null);
    setQboPushWarning(null);
    setQboPushTicketIds((current) => current.filter((id) => id !== ticketId));
    setQboPushVerificationRequired(false);
    const flush = flushPendingSave();
    if (flush) {
      await flush;
      if (!isCurrentSend()) return;
      if (saveStatusRef.current === 'error') {
        // Same silent-no-op guard as handleTransition: the save-error toast
        // may already have auto-dismissed by the time this button is tapped.
        showToast('Fix the save error first — this ticket has unsaved changes', 'error');
        return;
      }
      if (saveStatusRef.current === 'queued') {
        showToast('Saved offline — reconnect to send this ticket to QuickBooks', 'info');
        return;
      }
    }
    if (!isCurrentSend()) return;
    try {
      const res = await sendToQboMutation.mutateAsync([ticketId]);
      if (!isCurrentSend()) return;
      const label = res?.docType === 'invoice' ? 'Invoice' : 'Estimate';
      const num = res?.doc?.number ? ` #${res.doc.number}` : '';
      showToast(`${label}${num} created in QuickBooks`, 'success');
      const warning = extractBaserowSyncWarning(res);
      if (warning) {
        setQboPushWarning(warning);
        showToast(warning, 'warning');
      }
    } catch (err) {
      if (!isCurrentSend()) return;
      const recoveryReason = ticketRecoveryReason(err);
      if (recoveryReason) {
        enterRecoveryReason(recoveryReason);
        return;
      }
      const message = extractTransitionErrorMessage(err);
      if (isQboPushInProgressError(err)) {
        setQboPushWarning(message);
        setQboPushTicketIds((current) => current.includes(ticketId) ? current : [...current, ticketId]);
      } else {
        setQboPushError(message);
        showToast(message, 'error');
      }
    }
  }, [ticketId, sendToQboMutation, flushPendingSave]);

  const clearQboPushFeedback = useCallback(() => {
    setQboPushError(null);
    setQboPushWarning(null);
    setQboPushTicketIds((current) => current.filter((id) => id !== ticketId));
    setQboPushVerificationRequired(false);
  }, [ticketId]);

  const handleDeleteAttachment = useCallback(
    (attachmentId: string) => {
      if (!canEditSession()) return;
      const generation = formGenerationRef.current;
      const epoch = getAuthEpoch();
      const isCurrentOperation = () => formGenerationRef.current === generation && isCurrentAuthEpoch(epoch);
      deleteAttachment.mutate(attachmentId, {
        onSuccess: () => {
          if (isCurrentOperation()) showToast('Attachment deleted', 'success');
        },
        onError: (err) => {
          if (!isCurrentOperation()) return;
          showToast(
            err instanceof Error ? err.message : 'Failed to delete attachment',
            'error',
          );
        },
      });
    },
    [deleteAttachment],
  );

  const handleUpdateAttachmentType = useCallback(
    (attachmentId: string, attachmentType: 'site_photo' | 'receipt' | 'document') => {
      if (!canEditSession()) return;
      const generation = formGenerationRef.current;
      const epoch = getAuthEpoch();
      const isCurrentOperation = () => formGenerationRef.current === generation && isCurrentAuthEpoch(epoch);
      updateAttachment.mutate(
        { attachmentId, attachmentType },
        {
          onSuccess: () => {
            if (isCurrentOperation()) showToast('Attachment tag updated', 'success');
          },
          onError: (err) => {
            if (!isCurrentOperation()) return;
            showToast(
              err instanceof Error ? err.message : 'Failed to update attachment tag',
              'error',
            );
          },
        },
      );
    },
    [updateAttachment],
  );

  const handleUploadAttachment = useCallback(
    (file: File, attachmentType: 'site_photo' | 'receipt' | 'document' = 'site_photo') => {
      if (!canEditSession()) return;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('attachment_type', attachmentType);
      const generation = formGenerationRef.current;
      const epoch = getAuthEpoch();
      const isCurrentOperation = () => formGenerationRef.current === generation && isCurrentAuthEpoch(epoch);
      uploadAttachment.mutate(formData, {
        onSuccess: () => {
          if (isCurrentOperation()) showToast('Attachment uploaded', 'success');
        },
        onError: (err) => {
          if (!isCurrentOperation()) return;
          // ApiError is a plain { status, message, data } object, not an Error
          // instance — without checking .message directly the toast falls back
          // to the generic string and hides the real backend reason (e.g.
          // unsupported_media_type for iPhone camera HEIC uploads).
          const message =
            (typeof err === 'object' && err !== null && 'message' in err
              ? String((err as { message?: unknown }).message ?? '')
              : '') ||
            (err instanceof Error ? err.message : '') ||
            'Failed to upload attachment';
          showToast(message, 'error');
        },
      });
    },
    [uploadAttachment],
  );

  const handleDelete = useCallback(
    (options?: { onSuccess?: () => void }) => {
      if (!canEditSession()) return;
      const generation = formGenerationRef.current;
      const epoch = getAuthEpoch();
      const isCurrentOperation = () => formGenerationRef.current === generation && isCurrentAuthEpoch(epoch);
      deleteTicketMutation.mutate(undefined, {
        onSuccess: (response) => {
          if (!isCurrentOperation()) return;
          // The DELETE endpoint archives the ticket locally even when the
          // Trello summary-comment cleanup fails (Trello outage, missing
          // permission, comment already gone). Surface that distinction so
          // the user knows whether they need to manually tidy the Trello card.
          if (response?.trelloCommentDeleteFailed) {
            showToast(
              'Ticket archived, but the Trello summary comment couldn’t be removed. The next sync will retry; you can also delete the comment from Trello manually.',
              'warning',
            );
          } else {
            showToast('Ticket archived and removed from active views', 'success');
          }
          // The ticket is archived either way — let the host leave the view.
          options?.onSuccess?.();
        },
        onError: (err) => {
          if (!isCurrentOperation()) return;
          showToast(
            err instanceof Error ? err.message : 'Failed to archive ticket',
            'error',
          );
        },
      });
    },
    [deleteTicketMutation],
  );

  const handleUnarchive = useCallback(() => {
    if (!canEditSession()) return;
    const generation = formGenerationRef.current;
    const epoch = getAuthEpoch();
    const isCurrentOperation = () => formGenerationRef.current === generation && isCurrentAuthEpoch(epoch);
    unarchiveTicket.mutate(undefined, {
      onSuccess: (result) => {
        if (!isCurrentOperation()) return;
        showToast(
          `Ticket restored to ${result.restored_status.replace(/_/g, ' ')}`,
          'success',
        );
      },
      onError: (err) => {
        if (!isCurrentOperation()) return;
        const message =
          (typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message?: unknown }).message ?? '')
            : '') || 'Failed to unarchive ticket';
        showToast(message, 'error');
      },
    });
  }, [unarchiveTicket]);

  return {
    ticket,
    isLoading,
    isError,
    // Surfaced so renderers can distinguish a real 404 (dead end) from a
    // network/server failure (retryable) instead of showing "Not Found" for
    // both (#533).
    error,
    refetch,
    formState,
    fieldErrors,
    transitionRejectionCount,
    laborEntries,
    memberIds,
    materialEntries,
    equipmentEntries,
    isDirty,
    saveStatus,
    recovery,
    reloadRequired,
    isReloading: recoveryApi.isReloading,
    retainedDrafts,
    retainedDraftsMemoryOnly: retainedDrafts.length > 0 && ticketRetainedDraftsMemoryOnly(recoveryOwner, ticketId),
    restoreRetainedDraft,
    discardRetainedDraft,
    isLocked,
    isEditBlocked,
    isSessionEditBlocked: !canEditSession(),
    isQboLinked,
    canResyncQboTickets,
    qboEditEnabled,
    beginQboEdit,
    cancelQboEdit,
    isAwaitingManagerReview,
    canSelfRecall,
    canEditWorkspace,
    canViewRates,
    canPushToQbo,
    canConvertEstimate,
    canManageInvoices,
    canViewSales,
    canDelete,
    canDeleteAttachments,
    isManagerOrAdmin,
    attachments,
    attachmentsTotal,
    attachmentsHasMore,
    loadMoreAttachments,
    isSaving: updateTicket.isPending,
    isTransitioning: transitionTicket.isPending,
    isSendingToQbo: sendToQboMutation.isPending,
    qboPushError,
    qboPushWarning,
    qboPushInProgress,
    qboPushVerificationRequired: qboPushInProgress && qboPushVerificationRequired,
    isUploading: uploadAttachment.isPending,
    isDeletingAttachment: deleteAttachment.isPending,
    isUpdatingAttachment: updateAttachment.isPending,
    isDeletingTicket: deleteTicketMutation.isPending,
    isUnarchiving: unarchiveTicket.isPending,
    handleFieldChange,
    handleLaborChange,
    handleMembersChange,
    handleMaterialChange,
    handleEquipmentChange,
    handleSave,
    handleRetrySave,
    handleRestoreQuarantine,
    handleReloadLatest,
    handleBlurSave,
    flushPendingSave,
    handleTransition,
    handleSendToQbo,
    clearQboPushFeedback,
    handleDelete,
    handleUnarchive,
    handleDeleteAttachment,
    handleUpdateAttachmentType,
    handleUploadAttachment,
  };
}

export function validateTransitionFields(
  currentStatus: TicketStatus | undefined,
  targetStatus: TicketStatus,
  formState: UpdateTicketData,
  laborEntries: LaborEntry[],
  materialEntries: MaterialEntry[],
  equipmentEntries: EquipmentEntry[] = [],
  // Missing API metadata is fail-strict. Only an explicit false from the
  // server's successful non-OI resolution relaxes these two inline gates.
  openInvoiceBillingRequired = true,
): TicketFieldErrors {
  const errors: TicketFieldErrors = {};

  if (currentStatus === 'draft' && targetStatus === 'field_review') {
    if (!formState.description?.trim()) {
      errors.description = 'Description is required';
    }
    // Customer is intentionally NOT required to reach field_review (mirrors
    // backend validateForReviewSubmit). Techs often don't know the billing
    // customer in the field; it becomes required at ready_for_invoice below.
    if (!formState.work_date) {
      errors.work_date = 'Work date is required';
    }
    // Mirror backend validateForReviewSubmit (backend/src/utils/validation.js):
    // a ticket is billable with ANY one of labor (hours), a non-customer-provided
    // material, or equipment. Labor-only / material-only / equipment-only are all
    // valid. The removed "Labor Only" toggle no longer factors in.
    const hasLabor = laborEntries.some((entry) => Number(entry.hours) > 0);
    const hasBillableMaterial = materialEntries.some((m) => !m.is_customer_provided);
    const hasEquipment = equipmentEntries.length > 0;
    if (!hasLabor && !hasBillableMaterial && !hasEquipment) {
      errors.labor_entries =
        materialEntries.length > 0 && materialEntries.every((m) => m.is_customer_provided)
          ? 'Customer-provided materials are not billable. Add billable labor, billable materials, or equipment before submitting'
          : 'Add at least one labor entry, billable material, or equipment item before submitting';
    }
  }

  if (targetStatus === 'ready_for_invoice') {
    if (!formState.customer_id) {
      errors.customer_id = 'Customer is required';
    }
    // AFE gates OpenInvoice readiness (charge_code was consolidated into AFE in
    // Batch 2). This FE gate is intentionally STRICTER than the backend: it only
    // accepts a non-empty billing_afe, whereas backend validateForInvoice's
    // resolveAfeValue also resolves legacy charge_code fallbacks. The backend is
    // authoritative; this just gives early inline feedback in the common case.
    if (openInvoiceBillingRequired && !formState.billing_afe?.trim()) {
      errors.billing_afe = 'AFE is required';
    }
    if (openInvoiceBillingRequired && !formState.billing_requisitioner?.trim()) {
      errors.billing_requisitioner = 'Requisitioner is required';
    }

    // Row-level invoice readiness — mirrors backend validateForInvoice
    // (backend/src/utils/validation.js) so the manager sees WHICH line blocks
    // the invoice, highlighted inline, instead of a bare count toast.
    const material_rows: Record<number, MaterialRowErrors> = {};
    materialEntries.forEach((m, i) => {
      // Misc items no longer require manager approval before invoicing — the
      // review_required flag is informational only now (backend validateForInvoice
      // dropped the matching hard gate). A line still needs a price unless it has a
      // QBO catalog item to fall back on. Customer-provided lines never bill —
      // the QBO line builders skip them — so they need neither (#851).
      if (m.is_customer_provided) return;
      const hasPrice = m.unit_price != null || m.unit_price_snapshot != null;
      if (!hasPrice && !m.qb_item_id) {
        material_rows[i] = {
          ...material_rows[i],
          unit_price: 'Enter an amount — no QuickBooks price to fall back on',
        };
      }
    });
    if (Object.keys(material_rows).length > 0) {
      errors.material_rows = material_rows;
    }

    // A labor row with no explicit rate is NOT a blocker: it bills via the QBO
    // Labor item's catalog rate (mirrors backend validation.js +
    // buildInvoiceFromTicket), and a manager can override the rate per line in
    // the labor detail drawer.
  }

  return Object.fromEntries(
    Object.entries(errors).filter(([, value]) => Boolean(value)),
  ) as TicketFieldErrors;
}
